import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import PanelComponents from './PanelComponents'
import PanelAddOns from './PanelAddOns'
import AlertMessage from './AlertMessage';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import 'url-search-params-polyfill';
import withRequisitionStep from './components/withRequisitionStep';
import withWorkflow from './components/withWorkflow';
import { scrollToMessage } from './util/scrollToMessage';
import LoadOrderFromSearch from './panel/LoadOrderFromSearch';
import RequiredFieldDisclaimer from './components/RequiredFieldDisclaimer';
import OrderableItemList, {OrderableItemSummary} from './panel/OrderableItemList';
import { confirmPriceIncrease, fetchOmsRequisitionByOrderableItemId, fetchPractitionerOrderableItems } from './util/apiUtils';
import { cleanActivationCode, isCanadianClient, isGovernmentPayorOrder, isValidActivationCode, getRequisitionFromOrderByReqNumberOrActivationCode } from './util/gearOrderUtil';
import PanelCustomization from './panel/PanelCustomization';
import Modal from './components/Modal';
import MedicalNecessityDisclaimer, {MedicareObligationText} from './components/MedicalNecessityDisclaimer';
import MedicareChoice from './components/MedicareChoice';
import AccessionedOrderAlert from './components/AccessionedOrderAlert';
import { isMedicareSupported, isMedicareOrTricareOrderableItem, isPractitionerSupportsMedicare, isOrderRequisitionsMedicareEligible } from './util/MedicareUtil';
import ResubmittalPrompt, {ResubmittalLink} from './components/ResubmittalPrompt';
import { jsPDF } from 'jspdf';
import ProductHighlights from './panel/ProductHighlights';
import { buildInitialTestSelectionForm, getPaperlessSelections, getProfileComponentIds, getPanelIdsForComponents, isPossiblePriceIncrease } from './panel/panelSelectionUtil';
import ProfileDisclaimers from './panel/ProfileDisclaimers';
import SavedBundlesAccordion from './bundle/order/SavedBundlesAccordion';
import ClientActivationMissingMsg from './requisition-search/ClientActivationMissingMsg';
import RepurposeCollectionPack from './panel/RepurposeCollectionPack';
import { ComponentPrice, DisplayPricingChoice, getComponentPrice } from './panel/PanelPriceDisplay';

/**
 * Component for the 'panels' step of the GEAR order workflow.
 */
class Panels extends Component {
	constructor(props) {
		super(props);
		this.printRef = React.createRef()

		this.orderTypes = {
			BULK: "bulk",
			DROPSHIP: "dropShip",
			REGISTRATION: "registration",
			COVIDREGISTRATION: "covidRegistration",
			NO_CARD_ACTIVATION: "noCardRegistration",
			UNKNOWN_ID_ACTIVATION: "unknownIdActivation"
		}

		// Check if the page should be 'locked' (Requisition has been accessioned)
		let disableInputs = this.props.userInfo.adminUser !== 'true' && Boolean(this.props.activeGearRequisition && this.props.activeGearRequisition.accessionNo);
		const isShipToOffice = this.props.isShipToOfficeOrder(this.props.gearOrder);

		// Determine the initial order type
		let orderType = null;
		if (this.props.inlineOrderType) {
			orderType = this.props.inlineOrderType;
		} else if (this.props.userInfo.type === 'prc' && this.props.addNewReq) {
			orderType = this.orderTypes.REGISTRATION;
		} else if ( isShipToOffice ) {
			orderType = this.orderTypes.BULK;
		} else if ( this.props.gearOrder.myGDXKitOrder ) {
			orderType = this.orderTypes.DROPSHIP;
		}  else if ( this.props.gearOrder.covidRegistration ) {
			orderType = this.orderTypes.COVIDREGISTRATION;
		} else if (
			this.props.userInfo.type === 'prc' &&
			this.props.omsRequisition &&
			this.props.omsRequisition.orderableItem &&
			[902, 910].indexOf(this.props.omsRequisition.orderableItem.id) > -1
		) {
			orderType = this.orderTypes.COVIDREGISTRATION;
		} else if (this.props.gearOrder.recollectionFor) {
			orderType = this.orderTypes.DROPSHIP;
		} else if (this.props.activeGearRequisition && !this.props.addNewReq) {
			orderType = this.orderTypes.REGISTRATION;
		}

		// Allow DropShip for Full Option (reqLayoutId=0), Client Bill (reqLayoutId=2), or Canadian clients
		const office = this.props.gearOrder?.practitioner?.office;
		const displayDropShipOption = office?.reqLayoutId === 0 || office?.reqLayoutId === 2 || isCanadianClient(this.props.gearOrder);

		const simplifiedClientBilling = this.props.gearOrder.simplifiedClientBilling;
		const prcPaperless = !!(this.props.userInfo.type === 'prc' && this.props.omsRequisition?.paperless);

		// Pre-select Medicare Order based on existing order details
		// SCR-6809: Or pre-select "No" if the practitioner does not support Medicare
		let medicareOrder = (this.props.gearOrder.requisitions && this.props.gearOrder.requisitions.length) ? isGovernmentPayorOrder(this.props.gearOrder) : null;
		if (!medicareOrder && !isPractitionerSupportsMedicare(this.props.gearOrder?.practitioner)) {
			medicareOrder = false;
		}

		// If we were directed here from the manage bundles page we can assume the order type and medicare choice
		if (this.props.location?.state?.forwardFrom === 'bundles') {
			orderType = this.orderTypes.DROPSHIP;
			medicareOrder = false;
		}

		let xComId = this.props.activeGearRequisition?.xComId;
		// If there are no requisitions on the order, get the xComId from the order's recollectionFor value
		if (!xComId && !this.props.gearOrder.requisitions?.length) {
			xComId = this.props.gearOrder.recollectionFor;
		}

		this.state = {
			gearOrder: this.props.gearOrder,
			omsRequisition: this.props.omsRequisition,
			activeGearRequisition: this.props.activeGearRequisition,
			searchMessage: '',
			showReqImage: false,
			showReqTestSelectionsImage: false,
			disableInputs: disableInputs,
			searchOrderConflict: null,
			displayLoadOrder: false,
			searchForm: {
				requisitionNumber: {
					value: ""
				}
			},
			testSelectionForm: buildInitialTestSelectionForm(this.props.gearOrder, this.props.activeGearRequisition, this.props.omsRequisition, this.props.userInfo),
			medicalNecessityAcknowledged: {
				value: this.props.gearOrder.dateMedicalNecessityAcknowledged
			},
			fullOrderableItemsLists: null, // The full list of practitioner orderable items
			medicareOrderableItemsLists: null, // The full list of practitioner orderable items that support Medicare or Tricare
			filteredOrderableItemsList: null, // The list of practitioner orderable items, filtered by the search term
			profileFilter: '', // The orderable item search term
			profileSpecificFields: null,
			orderType: orderType,
			expandedOrderableItemList: 'recent',
			bulkOrderQuantityMap: this.getQuantityMapFromOrder(this.props.gearOrder),
			customizationOverlayProperties: {},
			displayDropShipOption: displayDropShipOption,
			medicareOrder: medicareOrder,
			simplifiedClientBilling: simplifiedClientBilling,
			submitDisabled: false, // Disable submit button while a submit is taking place
			cptQuantityMap: this.buildCptQtyMap(this.props.activeGearRequisition),
			isEditingCptQuantity: false,
			xComId: xComId,
			prcPaperless: prcPaperless,
			overCanadaQtyLimit: false,
		};
		
		this.validateSearch = this.validateSearch.bind(this);
		this.doReqSearch = this.doReqSearch.bind(this);
		this.onReqNumberChange = this.onReqNumberChange.bind(this);
		this.showReqImage = this.showReqImage.bind(this);
		this.setSelectedComponents = this.setSelectedComponents.bind(this);
		this.setSelectedAddOns = this.setSelectedAddOns.bind(this);
		this.selectPanel = this.selectPanel.bind(this);
        this.goBack = this.goBack.bind(this);
		this.validateTestSelection = this.validateTestSelection.bind(this);
        this.submitTestSelection = this.submitTestSelection.bind(this);
		this.fetchGearOrderForSearch = this.fetchGearOrderForSearch.bind(this);
		this.fetchOmsRequisitionForSearch = this.fetchOmsRequisitionForSearch.bind(this);
		this.setInputFieldValue = this.setInputFieldValue.bind(this);
		this.resolveOrderConflict = this.resolveOrderConflict.bind(this);
		this.fetchOrderableItems = this.fetchOrderableItems.bind(this);
		this.filterOrderableItems = this.filterOrderableItems.bind(this);
		this.searchByOrderableItem = this.searchByOrderableItem.bind(this);
		this.scrollToDetails = this.scrollToDetails.bind(this);
		this.getActiveGearRequisitionId = this.getActiveGearRequisitionId.bind(this);
		this.onAccordionToggle = this.onAccordionToggle.bind(this);
		this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
		
		this.loadOrder = this.loadOrder.bind(this);
		this.loadOrderModal = null;
		this.setLoadOrderModal = element => this.loadOrderModal = element;
	}

	/*
	 * When the component first mounts load the sample collection display.
	 */
	componentDidMount() {
		/*
		 * If this is a 'prc' user and there is a requisition number in the URL start the order search. 
		 * This will happen for patient-first registration
		 */
		const params = new URLSearchParams(this.props.location.search);
		let requisitionNumber = params.get('requisitionNumber');
		if (this.props.userInfo.type === 'prc' && requisitionNumber && !this.props.addNewReq && !this.props.omsRequisition) {
			this.fetchOmsRequisitionForSearch(requisitionNumber);
		} else {
			// Set up the req display using passed in data
			this.setInputFieldValue(this.props.omsRequisition, this.state.activeGearRequisition);

			// If we need to show the search controls fetch the orderable items lists
			if (this.props.userInfo.type !== 'prc' && !this.props.inlineOrderType) {
				this.fetchOrderableItems();
			}
		}
	}


	/**
	 * When the component unmounts turn off the logic to search and add a new req to an existing order.
	 */
	componentWillUnmount() {
		this.props.setAddNewReq(false);
	}

	componentDidUpdate() {
		//toggle display of Covid questions if test selection is made
		let profileSpecificDisplay = document.getElementById("profileSpecificFields");
		let covidDisplay = document.getElementById("covid19");
		let otherFields = this.state.profileSpecificFields && Object.keys(this.state.profileSpecificFields).filter(key=>key !== 'covid19');	
		if (this.state.profileSpecificFields && this.state.profileSpecificFields.covid19) {
			var covidSelectedTests = [];
			var panels = document.getElementsByName('panelGroup');
			for (var i=0; i < panels.length; i++) {
				if (panels[i].dataset && panels[i].dataset.text && panels[i].dataset.text.indexOf('SARS') > -1 && panels[i].checked) {
				  covidSelectedTests.push(panels[i].value);
			  }
			}
			
			var addOns = document.getElementsByName('addOnGroup');
			for (i=0; i < addOns.length; i++) {
			  if (addOns[i].dataset && addOns[i].dataset.text && addOns[i].dataset.text.indexOf('SARS') > -1 && addOns[i].checked) {
				  covidSelectedTests.push(addOns[i].value);
			  }
			}
			
			if (covidSelectedTests.length > 0) {
				profileSpecificDisplay.classList.remove('hidden');
				covidDisplay.classList.remove('hidden');	
			} else {	
				if (profileSpecificDisplay) {
					if (otherFields.length === 0) {
						profileSpecificDisplay.classList.add('hidden');
					} else {
						profileSpecificDisplay.classList.remove('hidden');
					}
				}
			 if (covidDisplay) {
				covidDisplay.classList.add('hidden');
			 }
			}
		} else {
			if (profileSpecificDisplay && otherFields.length > 0) {
				profileSpecificDisplay.classList.remove('hidden');
				
				if (covidDisplay) {
					covidDisplay.classList.add('hidden');
				}
			}
		}
	}
	
	/**
	 * Push a new history URL. Orders.js will notice the URL change and will load the new order.
	 */
	loadOrder(order, gearReqId) {
		this.props.history.push({
			pathname:'/order/' + (order.id || "new") + "/panels" + (gearReqId ? '/' + gearReqId : ''),
			search: this.props.location.search
		});
	}
	
	validateSearch(searchForm) {
		Object.values(searchForm).forEach(prop => {
			prop.valid = true;
			prop.message = "";
		});
		if (!searchForm.requisitionNumber.value 
			|| !searchForm.requisitionNumber.value.replace(/\.|-|#|\s/g,'') // Empty after stripping dot,  dash,  hash,  or  space
		) {
			searchForm.requisitionNumber.valid = false;
			searchForm.requisitionNumber.message = "Please enter a requisition number.";
		}
		
		return searchForm;
	}

	/*
	 * Fetches the lists of items the practitoiner can order.
	 * The response will contain two lists, recentleyOrderedItems and otherOrderableItems.
	 */
	fetchOrderableItems() {
		fetchPractitionerOrderableItems(this.props.gearOrder.practitioner.gsdlNo)
		.then(json => {
			let orderableItemsLists = json;

			this.setState({
				fullOrderableItemsLists: orderableItemsLists,
				// Create a full list of items supported by medicare. The full list or medicare list will be used depending on the Medicare Choice value
				medicareOrderableItemsLists: {
					recentlyOrderedItems: orderableItemsLists.recentlyOrderedItems ? orderableItemsLists.recentlyOrderedItems.filter(item => isMedicareOrTricareOrderableItem(item)) : [],
					otherOrderableItems: orderableItemsLists.otherOrderableItems ? orderableItemsLists.otherOrderableItems.filter(item => isMedicareOrTricareOrderableItem(item)) : [],
					categoryMap: !orderableItemsLists.categoryMap ? {} :
						Object.entries(orderableItemsLists.categoryMap)
						.reduce((categories, [key, value]) => ({ ...categories, [key]: value ? value.filter(item => isMedicareOrTricareOrderableItem(item)) : [] }), {}),
					topProducts: orderableItemsLists.topProducts ? orderableItemsLists.topProducts.filter(item => isMedicareOrTricareOrderableItem(item)) : []
				}
			});
			this.loadProductFromUrl(orderableItemsLists);
		})
		.catch(e => {
			// initialize an empty orderableItemsLists so the spinner is hidden
			this.setState({
				fullOrderableItemsLists: {}
			});
		});
	}

	/**
	 * If this is a new order and an orderableItemId was passed in on the URL
	 * add that product to the quantity map.
	 * This is to support "Order Now" functionality from product pages.
	 * 
	 * @param fullOrderableItemsLists The list of orderable items loaded for the signed in practitioner
	 */
	loadProductFromUrl = ( fullOrderableItemsLists ) => {
		if (this.props.match.params.id === 'new' && this.state.bulkOrderQuantityMap.length === 0) {
			const params = new URLSearchParams(this.props.location.search);
			const orderableItemId = Number(params.get('orderableItemId'));

			// Find the orderable item in the list so we can get its name
			if (orderableItemId) {
				let match = ((fullOrderableItemsLists || {}).recentlyOrderedItems || []).find(
					item => item.id === orderableItemId
				);
				if (!match) {
					match = ((fullOrderableItemsLists || {}).otherOrderableItems || []).find(
						item => item.id === orderableItemId
					);
				}
				if (match) {
					this.setState({
						bulkOrderQuantityMap: [
							{
								id: null,
								omsOrderableItemId: orderableItemId,
								omsOrderableItemName: match.name,
								quantity: 1,
								components: [],
								addons: [],
								panel: null,
								patientInstructions: ''
							}
						],
						orderType: this.orderTypes.BULK
					});
				}
			}
		}
	}

	/*
	 * Searches for a requisition.
	 * 
	 * @param event the HTML event that triggered the search
	 */
	doReqSearch(event) {
		event.preventDefault();
		this.setState({
			searchForm: this.validateSearch(this.state.searchForm)
		}, () => {
			if (Object.values(this.state.searchForm).every(prop => prop.valid)) {
				this.setState({
					gearOrder: this.props.gearOrder,
					searchMessage: '',
					orderableItemNotice: '',
					activeGearRequisition: null,
					testSelectionForm: {
						panel: {
							value: ""
						},
						components: {
							value: []
						},
						addOns: {
							value: []
						}
					},
					omsRequisition: null,
					disableInputs: false,
					searchOrderConflict: null
				});
				this.props.setDirty(false);
				let requisitionNumber = this.state.searchForm.requisitionNumber.value;
				this.fetchGearOrderForSearch( requisitionNumber );
			} else {
				scrollToMessage();
			}
		});
	}

	/**
	 * Fetches the omsRequisition being searched.
	 * If the omsRequisition is found set it in the state and search for the 
	 *  parent gearOrder.
	 * Display any errors encountered.
	 */
	fetchOmsRequisitionForSearch(requisitionNumber) {
		this.props.setLoading(true);
		// Pass selected GSDLNO to verify selected pract matches the req
		let gsdlNo = this.props.gearOrder.practitioner.gsdlNo;

		// encode the req number in case it has a '#' character
		fetch(process.env.REACT_APP_GEAR_REST_URL + '/requisition?reqNum=' + encodeURIComponent(requisitionNumber) + '&gsdlNo=' + gsdlNo, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				return Promise.reject({response: response});
			}
		})
		.then(json => {
			if (json && json.id) {
				this.props.setOmsRequisition( json );
				this.setState({
					omsRequisition: json
				});

				const prcPaperless = json.paperless && this.props.userInfo.type === 'prc';

				let activeGearRequisition = getRequisitionFromOrderByReqNumberOrActivationCode( this.state.gearOrder, requisitionNumber );
				let selectedPanel = activeGearRequisition ? activeGearRequisition.panel : '';
				let components = activeGearRequisition ? activeGearRequisition.components : [];

				// Pre-select profile and components for simplifiedClientBilling
				if ((prcPaperless || this.state.simplifiedClientBilling) && !selectedPanel && !components.length) {
					const selections = getPaperlessSelections(json);
					selectedPanel = selections.selectedPanel;
					components = selections.components;
				}
				
				let testSelectionForm = Object.assign({}, this.state.testSelectionForm);
				testSelectionForm.panel.value = selectedPanel;
				testSelectionForm.components.value = components;

				// Set up CovidRegistration when a Covid req is loaded for a PRC user
				let orderType = this.state.orderType;
				if (
					!orderType &&
					this.props.userInfo.type === 'prc' &&
					json.orderableItem &&
					[902, 910].indexOf(json.orderableItem.id) > -1
				) {
					orderType = this.orderTypes.COVIDREGISTRATION;
				}

				const xComId = json?.orderableItem?.inputFields?.find(field => field.id === 66)?.value;

				this.setState({
					activeGearRequisition: activeGearRequisition,
					testSelectionForm: testSelectionForm,
					orderType: orderType,
					cptQuantityMap: this.buildCptQtyMap(activeGearRequisition),
					xComId: activeGearRequisition?.xComId || xComId || this.state.xComId,
					prcPaperless: !!prcPaperless
				});

				// Get customized selections from omsRequisition when we don't have a Gear Requisition
				// SCR-6138: Do not load customizations for Medicare orders
				if( !this.state.medicareOrder && !activeGearRequisition && json.orderableItem && json.orderableItem.inputFields ) {
					const customizedSelections = json.orderableItem.inputFields.filter(field => field.value);
					if( customizedSelections && customizedSelections.length ) {
						const panel = customizedSelections.find( field => field.inputType === 'Panel');
						if( panel ) {
							/* Create a mock event to pass to selectPanel and call selectPanel to handle selecting
							 * the panel and sub-components */
							const mockEvent = {
								currentTarget: {
									value: panel.id + '',
									checked: true
								}
							}
							this.selectPanel( mockEvent );
						} else {
							const components = customizedSelections.filter( field => {
								return field.inputType === 'Analyte'
								&& ( !field.inputFieldParams
									|| !field.inputFieldParams.find( param => param.name === 'Add-On' && param.value === 'true') );
								});
								if( components && components.length ) {
									this.setSelectedComponents( components.map( comp => comp.id + '' ) );
								}
						}

						const addOns = customizedSelections.filter( field => {
							return field.inputType === 'Analyte'
								&& field.inputFieldParams
								&& field.inputFieldParams.find( param => param.name === 'Add-On' && param.value === 'true');
						});
						if( addOns && addOns.length ) {
							this.setSelectedAddOns( addOns.map( addOn => addOn.id + '' ) );
						}
					}
				}
				
				this.setInputFieldValue(this.state.omsRequisition, activeGearRequisition);
			}
		})
		.catch(e => {
			if (e.response) {
				if (e.response.status === 401) {
					this.props.history.push({pathname:'/login'});
					return;
				} else if (e.response.status === 403) {
					this.props.history.push({pathname:'/', search:'status=non-beta'});
					return;
				} else {
					e.response.json().then(json => {
						if (json?.error === 'ClientActivationMissingException') {
							this.setState({
								searchMessage: <ClientActivationMissingMsg activationNumber={requisitionNumber} />
							});
							return;
						}
						if (json.message) {
							if (e.response.status === 404 && this.props.userInfo.type === 'prc') {
								this.setState({
									searchMessage: (
										<span>
											Requisition {requisitionNumber} cannot be added to your current order. Please use the icons at the top of this
											page to continue your current order or{' '}
											<Link to="/">click here to start a new order</Link>.
										</span>
									)
								});
							} else {
								this.setState({
									searchMessage: json.message
								});
							}
						} else if (e.response.status === 404 && isValidActivationCode(requisitionNumber)) {
							if (this.props.userInfo.type === 'prc') {
								this.setState({
									searchMessage: <ClientActivationMissingMsg activationNumber={requisitionNumber} />
								})
								return;
							} else {
								this.setOrderType(this.orderTypes.UNKNOWN_ID_ACTIVATION);
								return;
							}
						} else if (e.response.status === 404 || e.response.status === 400) {
							this.setState({
								searchMessage: 'We could not find that activation number. Please check the number and contact Genova Diagnostics if you believe it is valid.'
							});
						} else {
							this.setState({
								searchMessage: 'An unexpected error has occurred. Please call us at 800-522-4762.'
							});
						}
					})
					.catch( e => {
						this.setState({
							searchMessage: 'An unexpected error has occurred. Please call us at 800-522-4762.'
						});
					});
				}
			} else {
				this.props.showError("An unexpected error has occurred. Please call us at 800-522-4762.");
			}
		})
		.finally( () => this.props.setLoading(false) );;
	}

	/**
	 * Fetches the GEAR order.
	 */
	fetchGearOrderForSearch( requisitionNumber ) {
		let reqRetired = false;
		let dateOfBirth = null;
		let gdxId = null;
        if (this.props.userInfo.type === 'prc') {
			const params = new URLSearchParams(this.props.location.search);
			dateOfBirth = params.get('dateOfBirth');
			gdxId = params.get('gdxId');
        }
		
		// encode the req number in case it has a '#' character
		fetch(process.env.REACT_APP_GEAR_REST_URL + "/order?requisitionNumber=" + encodeURIComponent(requisitionNumber)
			+ (dateOfBirth !== null ? "&dateOfBirth=" + dateOfBirth : "")
			+ (gdxId !== null ? "&gdxId=" + gdxId : ""), {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				if (response.status === 404) {
					this.setState({
						activeGearRequisition: null,
						testSelectionForm: {
							panel: {
								value: ''
							},
							components: {
								value: []
							},
							addOns: {
								value: []
							}
						}
					});
				} else if (response.status === 410) {
					response.text()
						.then(text => {
							if (text) {
								this.setState({ orderableItemNotice: text });
							} else {
								let searchMessage = 'This product is no longer available. Please contact client services for further assistance.';
								this.setState({
									searchMessage: searchMessage,
								});
							}
					})
					reqRetired = true;
				}
				else {
					response.json().then( json => {
						if( json.message ) {
							this.setState({
								searchMessage: json.message
							});
						} else if (response.status === 401) {
							this.props.history.push({pathname:'/login'});
						} else if (response.status === 403) {
							this.props.history.push({pathname:'/', search:'status=non-beta'});
						} else {
							this.setState({
								searchMessage: 'We apologize, we ran into an unexpected error ' +
									' while searching for this requisition.  If you continue to see this message, please contact us at 800-522-4762.'
							});
						}
					});
				}
			}
		})
		.then(json => {
			const currentOrder = this.props.gearOrder;
			// If the current order id does not match the returned order id show an error
			// Else if the returned order has an id (order was found) find the corresponding gear requistiion
			if (currentOrder.id && json && (currentOrder.id !== json.id)) {
				this.props.setOmsRequisition(null);

				let gearReq = getRequisitionFromOrderByReqNumberOrActivationCode(json, requisitionNumber);
				let searchOrderConflict = {
					reqNumber: gearReq?.id,
					order: json,
					requisitions: [],
					omsRequisition: this.state.omsRequisition
				}
				json.requisitions.forEach( req => {
					searchOrderConflict.requisitions.push( {
						reqNumber: req.reqNumber,
						reqName: req.omsOrderableItemName
					})
				})
				this.setState({
					searchOrderConflict: searchOrderConflict,
					activeGearRequisition: null,
					omsRequisition: null,
					testSelectionForm: {
						panel: {
							value: ''
						},
						components: {
							value: []
						},
						addOns: {
							value: []
						}
					},
					medicalNecessityAcknowledged: {
						value: currentOrder.dateMedicalNecessityAcknowledged
					}
				});
			} else {
				let searchedOrderId = json ? json.id : null;
				if( !currentOrder.id && searchedOrderId && !this.props.activeGearRequisitionId ) {
					let gearReq = getRequisitionFromOrderByReqNumberOrActivationCode(json, requisitionNumber);

					const displayLoadOrderMedicareDetails = {
						isMedicareSelected: this.state.medicareOrder,
						isOrderMedicare: isGovernmentPayorOrder(json),
						isOrderableItemMedicareEligible: isMedicareSupported(json),
						orderableItemName: gearReq?.omsOrderableItemName
					}

					// Show modal
					this.setState({ 
						displayLoadOrder: true,
						displayLoadOrderMedicareDetails: displayLoadOrderMedicareDetails,
						orderToLoad: json,
						reqToLoad: gearReq
					});
				} else {
				
					let gearOrder = this.props.gearOrder;
					/*
					 * Save the order for use later (when POSTING).
					 * This will be needed when saving the selections if the step is loaded as 'new' but a
					 *  req search returned an existing order.
					 */
					if( json && json.id ) {
						gearOrder = json;
						this.setState({gearOrder: gearOrder});
						// Check if the existing order should be 'locked'
						let stepIndex = json.workflow.steps.findIndex( step => step.id === this.props.workflowMetaData['practitioner_confirmation'].id);
						let practConfirmStep = json.workflow.steps[stepIndex];
						let disableInputs = this.props.userInfo.adminUser !== 'true' && practConfirmStep && practConfirmStep.completed;
						this.setState({
							disableInputs: disableInputs
						})
					}

					if (!reqRetired) {
						this.fetchOmsRequisitionForSearch( requisitionNumber );
					}
				}
			}
		})
		.catch(e => {
			this.props.showError("An unexpected error has occurred. Please call us at 800-522-4762.");
		})
		.finally(this.props.setLoading(false));
	}
	onReqNumberChange(event) {
		let searchForm = Object.assign({}, this.state.searchForm);
		searchForm.requisitionNumber.value = event.target.value;
		this.setState({
			searchForm: this.validateSearch(searchForm)
		});
	}
	onXComIdChange = (value) => {
		this.setState({ xComId: value });
	}
	showReqImage(e) {
		e.preventDefault();
		this.setState({showReqImage: true});
	}
	showReqTestSelectionsImage = (e) => {
		e.preventDefault();
		this.setState({ showReqTestSelectionsImage: true });
	}
    goBack(e){
        if (this.props.doConfirm(e)){
			this.props.updateStep(this.props.gearOrder, 'panel', 'Back', this.props.activeGearRequisitionId);
        } else {
            e.preventDefault();
        }
    }
    selectPanel(e) {
		var selectedPanel = e.currentTarget.value;
		var parentChecked = e.currentTarget.checked;
        this.props.setDirty(true);
        
		let testSelectionForm = Object.assign({}, this.state.testSelectionForm);
		
        if (parentChecked) {
			// if we're selecting a panel, remove all selected components and add the descendant 
        	// components from the new panel and update state
			var newPanelComponents = getProfileComponentIds(this.state.omsRequisition.orderableItem.profileList
				.find((profile) => profile.id.toString() === selectedPanel).profileComponents);

			testSelectionForm.panel.value = e.currentTarget.value;
			testSelectionForm.components.value = newPanelComponents;
        } else {
        	// otherwise, remove all selected components and update state
			testSelectionForm.panel.value = "";
			testSelectionForm.components.value = [];
		}

		this.setState({
			testSelectionForm: this.validateTestSelection(testSelectionForm)
		});
	}

	/**
	 * Resets the search parameters or redirects to a different order depending
	 * on the user's choice.
	 * The choice of 'switch' will redirect to a different order. Any other choice
	 * will reset the serach message and clear and focus on the search input.
	 * 
	 * @param event The HTML event that triggered this method
	 */
	resolveOrderConflict(event) {
		event.preventDefault();
		const choice = event.target.value;
		if( choice === 'switch' ) {
			/**
			 * Pass the search result details to the parent and update the route path.
			 * The Panels component has a key property set to the active req id.
			 * When the active req id changes the Panels component will re-render with the new details.
			 */
			const order = this.state.searchOrderConflict.order;
			this.props.setGearOrder( order );
			this.props.setActiveGearRequisitionId( this.state.searchOrderConflict.reqNumber );

			// Set addNewReq = false so withRequisitionStep will load the new OmsRequisition
			this.props.setAddNewReq(false);

			// Replace the first occurance of the order id in the route path with the new order id
			const newLoc = this.props.location.pathname.replace( this.props.gearOrder.id, this.state.searchOrderConflict.order.id );
			this.props.history.push({pathname: newLoc});
		} else {
			this.setState({
				searchOrderConflict: null,
				searchForm: { 
					requisitionNumber: {}
				},
				omsRequisition: null
			})
			if( this.searchInput ) {
				this.searchInput.focus();
			}
		}
	}

    setSelectedComponents(selectedComponents) {
		let testSelectionForm = Object.assign({}, this.state.testSelectionForm);
		testSelectionForm.components.value = selectedComponents;
		/**
		 * If the parent panel of any selected component is displayOnly then also select that panel.
		 * Otherwise, clear the panel selection. We know we can clear the panel because components 
		 * under a selected panel are disabled. If a component is being selected then either no 
		 * panel is selected, or a different panel is selected and we need to clear that.
		 */
		const profileList = this.state.omsRequisition?.orderableItem?.profileList;
		const parentPanelIds = getPanelIdsForComponents(selectedComponents, profileList).map(Number);
		/** 
		 * It's possible a component could be a child of multiple panels but that shouldn't happen 
		 * for displayOnly panels, so just grab the first one.
		 */
		const parentPanel = profileList?.find(profile => profile.id === parentPanelIds?.[0]);
		let formPanelValue = '';
		if (parentPanel?.id && parentPanel.displayOnly) {
			formPanelValue = parentPanel.id.toString();
		}

		testSelectionForm.panel.value = formPanelValue;
    	this.setState({
    		testSelectionForm: this.validateTestSelection(testSelectionForm)
    	});
    }
	setSelectedAddOns(selectedAddOns) {
		let testSelectionForm = Object.assign({}, this.state.testSelectionForm);
		testSelectionForm.addOns.value = selectedAddOns;
    	this.setState({
    		testSelectionForm: this.validateTestSelection(testSelectionForm)
    	});
	}
	updateCptQuantity = ( omsInputFieldId, quantity ) => {
		if (omsInputFieldId) {
			this.setState((prevState) => ({
				cptQuantityMap: {
					...prevState.cptQuantityMap,
					[omsInputFieldId]: quantity
				}
			}));
		}
	}
	setMedicalNecessityAcknowledged = () => {
		let medicalNecessityAcknowledged = Object.assign({}, this.state.medicalNecessityAcknowledged);
		medicalNecessityAcknowledged.value = !medicalNecessityAcknowledged.value ? new Date() : null;
    	this.setState({
    		medicalNecessityAcknowledged: this.validateMedicalNecessityAcknowledged(medicalNecessityAcknowledged)
    	});
    }
	validateTestSelection(testSelectionForm) {
		Object.values(testSelectionForm).forEach(prop => {
			prop.valid = true;
			prop.message = "";
		});

		/*
		 * SCR-7146: Validate for domestic and international req types.
		 * This works because international only has the "Ship to Office" and "Register Online" options. 
		 * Ship to Office does not do validation and Register Online for international is only applicable for activation-card requisitions.
		 */
		if ((testSelectionForm.panel.value === "" || testSelectionForm.panel.value === "0")
			&& testSelectionForm.components.value.length === 0 ) {
			
			testSelectionForm.panel.valid = false;
			testSelectionForm.panel.message = "Please select a test.";
			testSelectionForm.components.valid = false;
			testSelectionForm.components.message = "Please select a test.";
		}

		return testSelectionForm;
	}
	validateMedicalNecessityAcknowledged = (medicalNecessityAcknowledged) => {
		medicalNecessityAcknowledged.valid = true;
		medicalNecessityAcknowledged.message = '';

		// Validate medicalNecessityAcknowledged if this is a Medicare order and a practitioner user
		if (this.state.medicareOrder && this.props.userInfo.type === 'myGDX') {
			if (!medicalNecessityAcknowledged.value) {
				medicalNecessityAcknowledged.valid = false;
				medicalNecessityAcknowledged.message = "Please acknowledge you have read the definition of medical necessity.";
			}
		}

		return medicalNecessityAcknowledged;
	}
	submitTestSelection(event){
		(event?.preventDefault && event.preventDefault());
		if( this.state.disableInputs ) {
			// No back-end updates required
			this.props.updateStep(this.props.gearOrder, 'panel', 'Next', this.props.activeGearRequisitionId);
			return;
		}

		// Prevent double-click submit
		if (this.state.submitDisabled) {
			return;
		} else {
			this.setState({ submitDisabled: true });
		}
		
		this.setState({
			testSelectionForm: this.validateTestSelection(this.state.testSelectionForm),
			medicalNecessityAcknowledged: this.validateMedicalNecessityAcknowledged(this.state.medicalNecessityAcknowledged)
		}, () => {
				const testFormValid = Object.values(this.state.testSelectionForm).every(prop => prop.valid);
				const medicalNecessityValid = this.state.medicalNecessityAcknowledged.valid;
			if (testFormValid && medicalNecessityValid) {
				if (this.shouldWarnOfPriceIncrease()) {
					this.setState({
						showConfirmPriceIncrease: true,
						isConfirmedPriceIncrease: false,
						submitDisabled: false
					})
					return;
				}

				// profile specific fields have a data-fieldid set,
				//filter specific fields from other input by checking to see if dataset exists 
		        var sampleCollectionData=[];
		        var inputs = document.getElementsByTagName('input');
		        var input;
		       
				for(var i = 0; i < inputs.length; i++) {
					if(inputs[i].dataset && inputs[i].dataset.fieldid) {
						if(!inputs[i].disabled) {
							input = inputs[i];	
							var inputField={};
							if ((input.type === 'radio' || input.type === 'checkbox') && input.checked) {		
								inputField.id = input.dataset.fieldid;
								// For Pre and Post Provocation, use the input's value
								// For 'noHrt' use the value of 'false' to capture No Hormone Replacement Therapy
								// For all other selected radios or checkboxes set the value to 'true'
								if (input.id === 'pre-provocation' || input.id === 'post-provocation') {
									inputField.value = input.value;
								} else if(input.id !== "noHrt") {
									inputField.value = "true";
								} else {
									inputField.value = "false";
								}

								// When Covid collection type is selected as "Other", use the "Please Specify" text input value
								if (input.id === 'SampleCollectionType-OTHR') {
									inputField.value = this.state.profileSpecificFields?.covid19['Collection Type-Other_Specific'] ?? 'true';
								}
								sampleCollectionData.push(inputField);
							} else if (input.type === 'radio' && !input.checked) {
								inputField.id = input.dataset.fieldid;
								// Do not save 'false' for unchecked hrt inputs. We already handled hrt in the checked case
								if(input.id !== "hrt" && input.id !== "noHrt") {
									inputField.value = "false";
									sampleCollectionData.push(inputField);
								}
							}	
							else if(inputs[i].type !== 'radio' && inputs[i].type !== 'checkbox'  && inputs[i].value && inputs[i].value !== "") {
								inputField.id = input.dataset.fieldid;
								inputField.value = input.value;
								sampleCollectionData.push(inputField);
							}
						}	
					}
				}

				// If the req was customized from kitOrdering, include the customized patient instructions
				let patientInstructions = (this.state.activeGearRequisition || {}).patientInstructions || null;
				if( !patientInstructions ) {
					let customizedPatientInstructions = this.state.omsRequisition.orderableItem.inputFields
						.filter(field => field.value && field.name === 'Patient Instruction');
					if( customizedPatientInstructions && customizedPatientInstructions.length === 1 ) {
						patientInstructions = customizedPatientInstructions[0].value;
					}
				}

				// Build componentOptions with cptCode quantity and include componentOptions in request object
				let componentOptions = this.buildSelectedOptions(this.state.testSelectionForm.components.value);

				// Build addOnOptions with cptCode quantity and include addOnOptions in request object
				let addOnOptions = this.buildSelectedOptions(this.state.testSelectionForm.addOns.value);

				// The xComId saved with the OmsRequistion from a Kit Ordering customized order
				const kitOrderingXComId = this.state.omsRequisition?.orderableItem?.inputFields?.find(field => field.id === 66)?.value;

				let gearReqId = this.state.activeGearRequisition?.id;
				let reqNumber = this.state.omsRequisition.id;
				let activationCode = this.state.orderType === this.orderTypes.UNKNOWN_ID_ACTIVATION ? cleanActivationCode(this.state.searchForm?.requisitionNumber?.value) : null;

				/**
				 * When a pack is being repurposed we need to grab some details from the original requisition.
				 */
				let repurposedCollectionPack = false;
				if (this.state.repurposeDetails &&
					this.state.omsRequisition.orderableItem.id !== this.state.repurposeDetails.originalOrderableItemId &&
					this.state.orderType === this.orderTypes.REGISTRATION) {
					repurposedCollectionPack = true;
					reqNumber = this.state.repurposeDetails.reqNumber;
					activationCode = cleanActivationCode(this.state.repurposeDetails.activationCode);
					gearReqId = this.state.repurposeDetails.gearReqId;
				}

				let gearReq = {
						id: gearReqId,
						reqNumber: reqNumber,
						omsOrderableItemId: this.state.omsRequisition.orderableItem.id,
						panel: this.state.testSelectionForm.panel.value, 
				        components: this.state.testSelectionForm.components.value,
						componentOptions: componentOptions,
						addons: this.state.testSelectionForm.addOns.value,
						addOnOptions: addOnOptions,
						sampleCollectionData: sampleCollectionData,
						dropShip: !this.state.omsRequisition.id && this.state.orderType === this.orderTypes.DROPSHIP,
						covidRegistration:  this.state.orderType === this.orderTypes.COVIDREGISTRATION,
						patientInstructions: patientInstructions,
						billingMethod: this.state.activeGearRequisition?.billingMethod,
						routeNumber: this.state.activeGearRequisition?.routeNumber,
						clinicalFindings: this.state.activeGearRequisition?.clinicalFindings,
						xComId: this.state.xComId || kitOrderingXComId,
						noCardActivation: this.state.orderType === this.orderTypes.NO_CARD_ACTIVATION || this.state.orderType === this.orderTypes.UNKNOWN_ID_ACTIVATION,
						activationCode: activationCode,
						repurposedCollectionPack: repurposedCollectionPack
				};
				
				// Merge requisition into order and submit
				let gearOrder = Object.assign({}, this.state.gearOrder);

				// Set the billing method to Medicare if this order is flagged as a government payor order
				let medicareOrder = false;
				if (this.state.medicareOrder) {
					medicareOrder = true;

					// SCR-6245: Do not allow Medicare selection when Chelation Provocation Agent is present
					if (this.isShowMedicareChelationWarning()) {
						medicareOrder = false;
						// Switch the req to PTPAY unless a non-Medicare option is already selected
						if (gearReq.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE || !gearReq.billingMethod) {
							gearReq.billingMethod = process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE;
						}
					} else {
						gearReq.billingMethod = process.env.REACT_APP_BILLING_MEDICARE_VALUE;
						gearOrder.dateMedicalNecessityAcknowledged = this.state.medicalNecessityAcknowledged.value;
					}
				} else {
					gearOrder.dateMedicalNecessityAcknowledged = null;
					if (gearReq.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE) {
						gearReq.billingMethod = null;
					}
				}

				// Set the billing method to Client if Client is the only supported method for this order's practitioner
				if (!gearReq.billingMethod &&
					gearOrder.practitioner.billingMethods.length === 1 &&
					gearOrder.practitioner.billingMethods[0] === 'Client'
				) {
					gearReq.billingMethod = process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;
				}

				// SCR-7147: Always set the billing method to Client for international requisitions
				if (!gearReq.billingMethod && this.isOfficeUsingInternationalRequisitionLayout()) {
					gearReq.billingMethod = process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;
				}

				// Only pass in the active requisition (Other reqs on this order will not be updated)
				gearOrder.requisitions = [gearReq];

				// If the Medicare choice is being switched on an existing order, update all reqs with the new billing method
				// !! converts possible undefined values to Boolean
				if (!!isGovernmentPayorOrder(this.state.gearOrder) !== !!this.state.medicareOrder) {
					let additionalReqsToUpdate = this.state.gearOrder?.requisitions
						.filter((req) => req.id !== gearReq.id)
						.map((req) => {
							return {
								...req,
								billingMethod: medicareOrder ? process.env.REACT_APP_BILLING_MEDICARE_VALUE : null
							};
						});
					if (additionalReqsToUpdate?.length) {
						gearOrder.requisitions.push(...additionalReqsToUpdate);
					}
				}

				this.props.setLoading(true);
				fetch(process.env.REACT_APP_GEAR_REST_URL+'/testSelection' + this.props.location.search, {
					method: 'PUT',
					credentials: 'include',
					mode: 'cors',
					timeout:1000,
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
					},
		            body: JSON.stringify(gearOrder)
				})
				.then(response => {
					if (response.ok) {
						return response.json();
					} else {
						return Promise.reject({response: response});
					}
				})
				.then(json => {
					if (json && json.id) {
						this.props.setGearOrder(json);
						let activeReqId = this.getActiveGearRequisitionId( json );
						if (this.state.isConfirmedPriceIncrease) {
							// Trigger patient email
							confirmPriceIncrease(json.id, activeReqId, 'Panels');
						}

						this.props.setActiveGearRequisitionId( activeReqId );
		                this.props.setDirty(false);
						this.props.updateStep(json, 'panel', 'Next', activeReqId);
					} else {
						this.setState({ submitDisabled: false });
					}
				})
				.catch(error => { 
					this.props.showError("The network is currently unavailable. Please call us at 800-522-4762.");
					this.setState({ submitDisabled: false });
				})
				.finally(()=>{
					this.props.setLoading(false);
				});
			} else {
				this.setState({ submitDisabled: false });
				scrollToMessage();
			}
		});
	}

	/**
	 * Build the GearProfileOptions list to submit and include updated CPT Quantities if any quantity was edited.
	 * 
	 * @param selectedIds The list of selected inputField Ids (Either profile components or addOns)
	 */
	buildSelectedOptions = (selectedIds) => {
		let options = [];
		if (selectedIds?.length) {
			for (const componentId of selectedIds) {
				let cptQuantity;
				if (this.state.medicareOrder) {
					cptQuantity = this.state.cptQuantityMap?.[componentId]
				}
				options.push({
					omsInputFieldID: componentId,
					cptQuantity: cptQuantity
				})
			}
		}

		return options;
	}

	/**
	 * Save ship-to-office quantities and redirect to the next step
	 */
	submitBulkOrder = (event) => {
		event.preventDefault();
		if( this.state.disableInputs ) {
			// No back-end updates required
			this.props.updateStep(this.props.gearOrder, 'panel', 'Next');
			return;
		}

		let gearReqs = this.state.bulkOrderQuantityMap.map(item => {
			return {
				id: item.id, // GearRequisition ID
				omsOrderableItemId: item.omsOrderableItemId,
				dropShip: false,
				shipToOffice: true,
				quantity: item.quantity,
				components: item.components || [],
				addons: item.addons || [],
				panel: item.panel || null,
				patientInstructions: item.patientInstructions || null
			}
		});

		// Merge requisitions into order and submit
		let gearOrder = Object.assign({}, this.state.gearOrder);
		gearOrder.requisitions = gearReqs;

		fetch(process.env.REACT_APP_GEAR_REST_URL+'/testSelection/', {
			method: 'PUT',
			credentials: 'include',
			mode: 'cors',
			timeout:1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
            body: JSON.stringify(gearOrder)
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				return Promise.reject({response: response});
			}
		})
		.then(json => {
			if (json && json.id){
				this.props.setGearOrder(json);
                this.props.setDirty(false);
				this.props.updateStep(json, 'panel', 'Next');
            }
		})
		.catch(error => { 
			this.props.showError("The network is currently unavailable. Please call us at 800-522-4762.");
		});
	}

	/**
	 * Get the gear req id that should be active.
	 * First, return the gear req id if we know it.
	 * If not, return the gear req id for the req with a matching oms requisition number (if we have a req number)
	 * If not, return the id of the last gear req added to the order
	 */
	getActiveGearRequisitionId( gearOrder ) {
		if( gearOrder.containsShipToOfficeRequisition ) {
			return null;
		}
		if( this.state.activeGearRequisition && this.state.activeGearRequisition.id ) {
			return this.state.activeGearRequisition.id;
		}
		let gearReqId;
		if( this.state.omsRequisition && this.state.omsRequisition.id ) {
			let req = gearOrder.requisitions.find( req => Number(req.reqNumber) === Number(this.state.omsRequisition.id) );
			if( req ) {
				gearReqId = req.id;
			}
		}
		
		// Default to the last req created (highest req id)
		if( !gearReqId ) {
			gearReqId = Math.max.apply(Math, gearOrder.requisitions.map( req => {
				if( !isNaN( req.id ) ) {
					return req.id;
				}
				// Default to 0 for undefined id values
				return 0;
			} ) );
		}
		
		return gearReqId;
	}

	/**
	 * Update the state values for compUrine provocation fields when values change
	 */
	updateCompUrineProvocation = (e) => {
		let profileSpecificFields = JSON.parse( JSON.stringify( this.state.profileSpecificFields ) );
		const fieldName = e.target.getAttribute('data-fieldname');
		if (fieldName === 'preProvocation') {
			// Toggle radio off if it is already selected
			profileSpecificFields.compUrine.preProvocation.value = profileSpecificFields.compUrine.preProvocation.value ? '' : '@PRE';
			profileSpecificFields.compUrine.postProvocation.value = '';
		} else if (fieldName === 'postProvocation') {
			profileSpecificFields.compUrine.preProvocation.value = '';
			// Toggle radio off if it is already selected
			profileSpecificFields.compUrine.postProvocation.value = profileSpecificFields.compUrine.postProvocation.value ? '' : '@POST';
		}

		this.setState({
			profileSpecificFields: profileSpecificFields
		});
	}

	/**
	 * Update the state values for Covid Specimen Collection Type fields when values change
	 */
	updateCovidCollectionType = (e) => {
		let profileSpecificFields = { ...this.state.profileSpecificFields };
		// Use the fieldName to know which option is selected. Example: "SampleCollectionType-OTHR" -> "Collection Type-Other"
		const fieldName = e.target.getAttribute('data-fieldname');
		let selectedField = fieldName?.split('-')[1];

		// Convert boolean to String because the inputs expect a String
		profileSpecificFields.covid19['Collection Type-Nasopharyngeal (NP)'].value = selectedField === 'NP' ? 'true' : 'false';
		profileSpecificFields.covid19['Collection Type-Oropharyngeal (OP)'].value = selectedField === 'OP' ? 'true' : 'false';
		profileSpecificFields.covid19['Collection Type-Nasal Anterior'].value = selectedField === 'NA' ? 'true' : 'false';
		profileSpecificFields.covid19['Collection Type-Nasal Midtubinar'].value = selectedField === 'NM' ? 'true' : 'false';
		profileSpecificFields.covid19['Collection Type-Other'].value = selectedField === 'OTHR' ? 'true' : 'false';

		this.setState({
			profileSpecificFields: profileSpecificFields
		});
	}

	/**
	 * Update state when the Other_Specific field changes
	 */
	handleCovidCollectionTypeOther = (e) => {
		let profileSpecificFields = { ...this.state.profileSpecificFields };
		profileSpecificFields.covid19['Collection Type-Other_Specific'] = e.target.value;

		this.setState({
			profileSpecificFields: profileSpecificFields
		})
	}

	handleDownloadPdf = async () => {
		this.props.setLoading(true);

		/* Set printTestSelections true to adjust test selection checkbox position.
		 * The call doDownloadPdf to build and save a PDF of the selections. */
		this.setState({
			printTestSelections: true
		}, () => {
			this.doDownloadPdf();
		})
	}

	doDownloadPdf = async () => {
		const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: 'a3', precision: 4 });
		let data = this.printRef.current;
		
		/* Build and save the test selections PDF and then set printTestSelections false to revert
		 * the checkboxes back to their original positions */
		pdf.html(data, {
			margin: [0, 0, 0, 50]
		}).then(() => {
			pdf.save("test-selection.pdf");
		})
		.finally(() => {
			this.setState({
				printTestSelections: false
			});
			this.props.setLoading(false);
		})
	}

	/**
	 * Loop through the oms requisition's orderableItem.inputFields and set up the 
	 * profile-specific field details. 
	 * If the activeGearRequisition contains a value for a profile-specific field 
	 * the input will be updated to reflect the active req, otherwise, default values
	 * will be used.
	 * 
	 * @param omsRequisition The OMS requisition structure
	 * @param activeGearRequisition The active gear requisition (can be null)
	 */
	setInputFieldValue(omsRequisition, activeGearRequisition) {
		let gearReqOrderableItemId = (activeGearRequisition || {}).omsOrderableItemId;
		let omsReqOrderableItemId = ( (omsRequisition || {}).orderableItem || {} ).id;
		if( !omsRequisition || ( this.props.addNewReq && gearReqOrderableItemId === omsReqOrderableItemId ) ) {
			if( this.state.profileSpecificFields !== null ) {
				this.setState({
					profileSpecificFields: null
				});
			}
			return;
		}

		let profileSpecificFields = null;
		let inputFields = [];
		let inputFieldValues = [];
		if ( omsRequisition.orderableItem ) {
			inputFields = omsRequisition.orderableItem.inputFields;
		}
		if ( activeGearRequisition && activeGearRequisition.profileSpecificFields ) {
			inputFieldValues = activeGearRequisition.profileSpecificFields;
		}

		let accessionKeys= [];
		let accessionIds= [];
		
		for(var i = 0; i < inputFields.length; i++) {
			if(inputFields[i].accessionKey !== "") {//accessionKey is set for all profileSpecificFields
				let accKey = inputFields[i].accessionKey;
				let fieldValues = {};
				
				// get stored value if there is one
				var found = false;
				for(var x = 0; x < inputFieldValues.length; x++) {
					let inputFieldValue = inputFieldValues[x];
					if (inputFieldValue.accessionKey === accKey) {
						found = true;
						fieldValues = inputFieldValue;
						break;
					}
				}
				
				// set default value from original list
				if (!found) fieldValues = inputFields[i];
				accessionKeys.push(accKey);
				accessionIds.push({accessionKey : accKey, fieldValues: fieldValues});
			}	
		}

		// HRT
		if (accessionKeys.indexOf("Hormone Replacement Therapy") > -1) {
			profileSpecificFields = profileSpecificFields || {};
			// Get the field values, or an empty object if the accessionKey isn't found
			let fieldData = ( ( accessionIds[accessionKeys.indexOf("Hormone Replacement Therapy")] || {} ).fieldValues || {} );
			profileSpecificFields.collectionHormoneTherapy = {
				hrt: {
					fieldId: fieldData.id,
					checked: fieldData.value === "true"
				},
				noHrt: {
					fieldId: fieldData.id,
					checked: fieldData.value === "false"
				}
			};	
		}

		// Cycle Phase
		if (accessionKeys.indexOf("Premenopausal") > -1) {
			profileSpecificFields = profileSpecificFields || {};
			// Get the field values, or an empty object if the accessionKey isn't found
			let premenopausalFieldValue = ( (accessionIds[accessionKeys.indexOf("Premenopausal")] || {} ).fieldValues || {} );
			let menopausalNoHRT = ( (accessionIds[accessionKeys.indexOf("Menopausal w/o HRT")] || {} ).fieldValues || {} );
			let menopausalHRT = ( (accessionIds[accessionKeys.indexOf("Menopausal w/ HRT")] || {} ).fieldValues || {} );
			let menopausalWoman = ((accessionIds[accessionKeys.indexOf("Menopausal woman")] || {}).fieldValues || {});
			profileSpecificFields.cyclePhase = {};
			if (premenopausalFieldValue.id) {
				profileSpecificFields.cyclePhase.premenopausal = {
					fieldId: premenopausalFieldValue.id,
					checked: premenopausalFieldValue.value === "true"
				}
			}
			if(menopausalNoHRT.id) {
				profileSpecificFields.cyclePhase.menopausalNoHRT = {
					fieldId: menopausalNoHRT.id,
					checked: menopausalNoHRT.value === "true"
				}
			}
			if(menopausalHRT.id) {
				profileSpecificFields.cyclePhase.menopausalHRT = {
					fieldId: menopausalHRT.id,
					checked: menopausalHRT.value === "true"
				}
			}
			if(menopausalWoman.id){
				profileSpecificFields.cyclePhase.menopausalWoman = {
					fieldId: menopausalWoman.id,
					checked: menopausalWoman.value === "true"
				}
			}
		}

		// "Comprehensive Urine Elements/Toxic Element Clearance (24 Hour or Timed)*"
		if(accessionKeys.indexOf("Pre-provocation")>-1) {
			profileSpecificFields = profileSpecificFields || {};
			// Get the field values, or an empty object if the accessionKey isn't found
			let _preProvFieldValue = accessionIds[accessionKeys.indexOf("Pre-provocation")]?.fieldValues || {};
			let _postProvFieldValue = accessionIds[accessionKeys.indexOf("Post-provocation")]?.fieldValues || {};

			profileSpecificFields.compUrine = {
				'preProvocation': {
					fieldId: _preProvFieldValue.id,
					value: _preProvFieldValue.value && _preProvFieldValue.value !== 'false' ? "@PRE" : ""
				},
				'postProvocation': {
					fieldId: _postProvFieldValue.id,
					value: _postProvFieldValue.value && _postProvFieldValue.value !== 'false' ? "@POST" : ""
				}
			};
		}

		// "COVID-19 (SARS Co-V-2) #1015" and "SARS-CoV-2 IgG #1016"
		// Diagnosed yes/no is not on req #1015
		
		if(accessionKeys.indexOf("First Test-yes")>-1) {
			profileSpecificFields = profileSpecificFields || {};

			const keys = ['Diagnosed-yes', 'Diagnosed-no',
				'First Test-yes', 'First Test-no', 'First Test-unknown',
				'Healthcare-yes', 'Healthcare-no', 'Healthcare-unknown',
				'Symptoms-yes', 'Symptoms-no', 'Symptoms-unknown', 'Symptoms Date',
				'Hospitalized-yes', 'Hospitalized-no', 'Hospitalized-unknown',
				'ICU-yes', 'ICU-no', 'ICU-unknown',
				'Resident-yes', 'Resident-no', 'Resident-unknown',
				'Pregnant-yes', 'Pregnant-no', 'Pregnant-unknown',
				'Onset of Illness-yes', 'Onset of Illness-no', 'Onset of Illness-unknown',
				'Inflammatory in Child-yes', 'Inflammatory in Child-no', 'Inflammatory in Child-unknown',
				'Collection Type-Other', 'Collection Type-Nasal Midtubinar', 'Collection Type-Nasal Anterior', 'Collection Type-Oropharyngeal (OP)', 'Collection Type-Nasopharyngeal (NP)'];

			// Get the id and value for each accessionKey and create a new profileSpecificFields.covid19 object
			keys.forEach(k => {
				let fieldValues = (accessionIds[accessionKeys.indexOf(k)] || {}).fieldValues;
				if (fieldValues) {
					profileSpecificFields.covid19 = profileSpecificFields.covid19 || {};
					profileSpecificFields.covid19[k] = {
						fieldId: fieldValues.id,
						value: fieldValues.value
					}

					//Other_Specific
					if (k === 'Collection Type-Other' && fieldValues.value && fieldValues.value !== 'false' && fieldValues.value !== 'true') {
						profileSpecificFields.covid19[k].value = 'true';
						profileSpecificFields.covid19['Collection Type-Other_Specific'] = fieldValues.value;
					}
				} 
			});
		}
        
		this.setState({
			profileSpecificFields: profileSpecificFields,
		});
		this.scrollToDetails();
	}

	/**
	 * Filters the orderable items list by the filter search term.
	 * If the search term is empty reset the filtered list to the full list.
	 * 
	 * @param event the HTML event that triggered this method
	 */
	filterOrderableItems(event) {
		event.preventDefault();
		// Get either the full list or the Medicare supported list of items, depending on the state.medicareOrder value
		let effectiveOrderableItemsLists = this.getEffectiveOrderableItemsLists();
		if( !effectiveOrderableItemsLists ) {
			return;
		}

		let profileFilter = event.target.value;		
		let expandedList = this.state.expandedOrderableItemList;
		let filteredList;
		if (!profileFilter) {
			if (expandedList === 'filtered') {
				expandedList = 'recent';
			}
			filteredList = null;
		} else {
			expandedList = 'filtered';
			const lcFilter = profileFilter.toLowerCase();
			filteredList = effectiveOrderableItemsLists.otherOrderableItems.filter( item => item.name.toLowerCase().includes( lcFilter ) || item.category.toLowerCase().includes( lcFilter ) );
		}
		this.setState({
			profileFilter: profileFilter,
			filteredOrderableItemsList: filteredList,
			expandedOrderableItemList: expandedList
		});
	}

	/**
	 * Returns the Orderable Items object to use as the base for filtering. The returned object will exclude items that
	 * do not support Medicare when the Medicare Yes/No choice is Yes.
	 */
	getEffectiveOrderableItemsLists = () => {
		return (this.isShowMedicareProducts() && this.state.medicareOrder) ? this.state.medicareOrderableItemsLists : this.state.fullOrderableItemsLists;
	}

	/**
	 * Fetches the oms requisition structure for the orderable item.
	 * 
	 * @param orderableItemId The orderableItemId to search
	 */
	searchByOrderableItem(orderableItemId) {
		// SCR-6785: Show a warning and do not allow the same item to be ordered more than once
		const reqWithSameItemId = this.props.gearOrder.requisitions?.find(req => req.omsOrderableItemId === orderableItemId);
		if (reqWithSameItemId) {
			this.setState({
				duplicateGearReq: reqWithSameItemId,
				omsRequisition: null,
				profileSpecificFields: null
			}, () => {
				this.scrollToDetails();
			});
			return;
		}
		this.setState({ duplicateGearReq: null });

		this.props.setLoading(true);
		let gsdlNo = this.props.gearOrder.practitioner.gsdlNo;
		fetch(process.env.REACT_APP_GEAR_REST_URL + '/requisition/getReqByOrderableItem?orderableItemId=' + orderableItemId + '&gsdlNo=' + gsdlNo, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				return Promise.reject({response: response});
			}
		})
		.then(json => {
				// Copy over some original values when repurposing a collection pack
				if (json && this.state.repurposeDetails) {
					json.paperless = true;
					json.activationCode = this.state.repurposeDetails.activationCode
				}
				this.props.setOmsRequisition( json );
				this.setState({
					omsRequisition: json,
					disableInputs: false,
					searchOrderConflict: null,
					activeGearRequisition: null,
					testSelectionForm: {
						panel: {
							value: ""
						},
						components: {
							value: []
						},
						addOns: {
							value: []
						}
					},
				});

				this.setInputFieldValue(this.state.omsRequisition, null);
			
		})
		.catch(e => {
			this.props.showError("An unexpected error has occurred. Please call us at 800-522-4762.");
		})
		.finally( () => this.props.setLoading(false) );
	}

	/**
	 * Scroll the browser to the requisition-details section.
	 */
	scrollToDetails() {
		// Prioritize the details section. If it is not found scroll to the first visible .alert.
		let reqDetails = document.querySelector("#requisition-details");
		if (!reqDetails) {
			reqDetails = document.querySelector(".alert:not([hidden])");
		}
		if (reqDetails){
			// Subtract 100 to make room for the website's floating header
			window.scroll({
				top: reqDetails.getBoundingClientRect().top + window.scrollY - 100, 
				left: 0, 
				behavior: 'smooth'
			});
		}
	}
	
	onAccordionToggle(listId) {
		let newExpandedList = listId;
		if( this.state.expandedOrderableItemList === listId ) {
			newExpandedList = null;
		}
		this.setState({expandedOrderableItemList: newExpandedList});
	}

	/**
	 * Shows or hides the bulk order customization modal and fetches the oms requisition when needed.
	 * 
	 * @param orderableItemId The ID of the orderable item to customize, or null if the customization should be hidden
	 */
	toggleCustomizationOverlay = (orderableItemId) => {
		// Clear previous, unsaved customizations
		this.setState({ customizedSelections: null});

		let newProp = this.state.customizationOverlayProperties[orderableItemId];
		if( !newProp ) {
			newProp = {
				'orderableItemId': orderableItemId
			}
		}

		newProp = {
			...newProp,
			'display': true,
			'status': newProp.omsRequisition ? null : 'loading'
		};

		let newOverlayProperties = {[orderableItemId]: newProp};
		// Turn off the display and status values for any other overlays
		for( const [id, prop] of Object.entries(this.state.customizationOverlayProperties) ) {
			if( Number(id) !== orderableItemId ) {
				newOverlayProperties[id] = {...prop, display: false, status: null};
			}
		}

		// Track the most recently customized orderable item
		newOverlayProperties['current'] = orderableItemId;

		this.setState({customizationOverlayProperties: newOverlayProperties});

		// Return if we already have the omsRequisition or there is no req to fetch
		if( newProp.omsRequisition || !orderableItemId ) {
			return;
		}

		// Fetch the oms requisition and update the overlay's status and error message as appropriate
		const gsdlNo = (this.props.gearOrder.practitioner || {}).gsdlNo;
		fetchOmsRequisitionByOrderableItemId( orderableItemId, gsdlNo )
		.then(json => {
			let newOverlayProperties = JSON.parse(JSON.stringify(this.state.customizationOverlayProperties));
			newOverlayProperties[orderableItemId] = {
				...newOverlayProperties[orderableItemId],
				status: null,
				omsRequisition: json
			};

			this.setState({customizationOverlayProperties: newOverlayProperties});
		}).catch(e => {
			let errorMessage = e.message;
			if( !errorMessage ) {
				errorMessage = 'An unexpected error has occurred. Please call us at 800-522-4762.';
			}
			
			let newOverlayProperties = JSON.parse(JSON.stringify(this.state.customizationOverlayProperties));
			newOverlayProperties[orderableItemId] = {
				...newOverlayProperties[orderableItemId],
				status: 'error',
				errorMessage: errorMessage
			};

			this.setState({customizationOverlayProperties: newOverlayProperties});
		});
	}	

	/**
	 * Stores the customization details anytime a selection is made in the customization modal.
	 * 
	 * @param selections The selected customizations object. This object contains the following:
	 *						orderableItemId - The ID of the orderable item being customized
	 *						selectedPanel - The selected panel for the requisition
	 *						selectedComponents - The selected panel components for the requisition
	 *						selectedAddOns - The selected panel addons for the requisition
	 *						patientInstructions - The patientInstructions for the requisition
	 */
	onCustomizationSelection = (selections) => {
		this.setState({ customizedSelections: selections });
		this.props.setDirty(true);
	}

	/**
	 * Find and update the appropriate bulk order item with the customization selections.
	 * NOTE: Using bulkOrderQuantityMap instead of gearOrder.requisitions means the customization will only work
	 *       for ship-to-office orders.
	 */
	onCustomizationSave = () => {
		this.props.setDirty(false);
		if( !this.state.customizedSelections ) {
			return;
		}

		let newBulkOrderMap = this.state.bulkOrderQuantityMap.map(item => {
			if( item.omsOrderableItemId !== this.state.customizedSelections.orderableItemId ) {
				return {...item};
			} else {
				return {
					...item,
					components: this.state.customizedSelections.selectedComponents || [],
					addons: this.state.customizedSelections.selectedAddOns || [],
					panel: this.state.customizedSelections.selectedPanel || null,
					patientInstructions: this.state.customizedSelections.patientInstructions || null
				}
			}
		});

		this.setState({bulkOrderQuantityMap: newBulkOrderMap});
	}

	/**
	 * Updates the quantity value of an OrderableItem in the bulkOrderQuantityMap, or inserts
	 * a new item in the map if is was not in the map already.
	 * Sets the new quantity map in state
	 * @param quantity The updated quantity of the OrderableItem
	 * @param item The OrderableItem being updated
	 */
	onQuantityUpdate({ quantity, item }) {
		if (quantity > 99) {
			quantity = 99;
		}

		let newItem = {
			id: item.id, // GearRequisition ID
			omsOrderableItemId: item.omsOrderableItemId,
			omsOrderableItemName: item.omsOrderableItemName,
			quantity: quantity,
			components: item.components || [],
			addons: item.addons || [],
			panel: item.panel || null,
			patientInstructions: item.patientInstructions || null
		};

		// Find the GEAR requisition ID matching this orderable item if one exists
		// No need to find the req ID if quantity = 0 because the item is being removed from the quantity map
		if( quantity > 0 && !item.id ) {
			newItem.id = this.findRequisitionIdByOrderableItemId( newItem.omsOrderableItemId, this.props.gearOrder );
		}

		// Create a new copy of the quantity map (so we don't mutate state)
		// Use the updated quantity if the OrderableItem being updated is already in the map
		let updateExistingItem = false;
		let newMap = this.state.bulkOrderQuantityMap.map(i => {
			if( i.omsOrderableItemId === item.omsOrderableItemId ) {
				updateExistingItem = true;
				return newItem;
			}
			return i;
		});

		// The OrderableItem being updated was not already in the quantity map, add it
		if( !updateExistingItem ) {
			newMap.push(newItem);
		}

		// SCR-8019: Limit Canada orders to <= 25 total packs
		let overCanadaQtyLimit = false;
		if (isCanadianClient(this.props.gearOrder)) {
			const totalQuantity = newMap.reduce((sum, i) => {
				let itemQuantity = +(i.quantity || 0);
				return sum + itemQuantity;
			}, 0);
			if (totalQuantity > 25) {
				// Find item being updated and set quantity to (25 - other quantities)
				const itemBeingUpdated = newMap.find((i) => i.omsOrderableItemId === item.omsOrderableItemId);
				if (itemBeingUpdated) {
					const maxQuantityAvailableForItem = 25 -
					newMap
						.filter((i) => i.omsOrderableItemId !== item.omsOrderableItemId)
						.reduce((sum, i) => {
							let itemQuantity = +(i.quantity || 0);
							return sum + itemQuantity;
						}, 0);
					itemBeingUpdated.quantity = maxQuantityAvailableForItem.toString();
				}
				// Set flag to show message
				overCanadaQtyLimit = true;
			}
		}

		// Only include items with a quantity greater than 0 in the state's quantity map
		this.setState({ bulkOrderQuantityMap: newMap.filter(i=> Number(i.quantity) > 0), overCanadaQtyLimit: overCanadaQtyLimit });
	}

	/**
	 * Loop through the requisitions on the GEAR order and build a quantity map to use for ship-to-office quantities.
	 * @param gearOrder The GEAR order containing previously saved requisitions
	 */
	getQuantityMapFromOrder = gearOrder => {
		if (gearOrder && gearOrder.requisitions) {
			if( gearOrder.requisitions.length ) {
				return gearOrder.requisitions.map( req => {
					return {
						id: req.id,
						omsOrderableItemId: req.omsOrderableItemId,
						omsOrderableItemName: req.omsOrderableItemName,
						quantity: req.quantity,
						components: req.components || [],
						addons: req.addons || [],
						panel: req.panel || null,
						patientInstructions: req.patientInstructions
					}
				});
			} else {
				// const params = new URLSearchParams(this.props.location.search);
				// const orderNowId = Number(params.get('orderableItemId'));
				// if (orderNowId) {
				// return [{
				// id: null,
				// omsOrderableItemId: orderNowId,
				// omsOrderableItemName: 'Testing query param',
				// quantity: 1,
				// components: [],
				// addons: [],
				// panel: null,
				// patientInstructions: ''
				// }]
				// }
			}
		}
		return [];
	}

	/**
	 * Finds a requisition in the GEAR order that has a matching omsOrderableItemId.
	 * This is so we can keep track of the GearRequisition Id when updating a quantity
	 * @param omsOrderableItemId The OrderableItem Id to match
	 * @param gearOrder The GEAR order containing previously saved requisitions
	 */
	findRequisitionIdByOrderableItemId = (omsOrderableItemId, gearOrder) => {
		let gearRequisitionId;
		if( omsOrderableItemId && gearOrder && gearOrder.requisitions ) {
			const matchingReq = gearOrder.requisitions.find( req => req.omsOrderableItemId === omsOrderableItemId );
			if( matchingReq ) {
				gearRequisitionId = matchingReq.id;
			}
		}
		return gearRequisitionId;
	}

	/**
	 * Set the orderType state value and clear out values from previous selections.
	 * If the order type is switched to or from the Covid Registration type, update the
	 * filtered items list.
	 *
	 * @orderType The orderType to set
	 */
	setOrderType = (orderType) => {

		// If we're switching to/from the Covid Regsitration type we need to filter the orderable item list appropriately
		const isCovid = orderType === this.orderTypes.COVIDREGISTRATION;
		let profileFilter = this.state.profileFilter;
		if (isCovid) {
			profileFilter = 'COVID';
		} else if (this.state.orderType === this.orderTypes.COVIDREGISTRATION) {
			profileFilter = '';
		}

		this.setState(
			(prevState) => {
				return {
					orderType: orderType,
					omsRequisition: null,
					searchMessage: null,
					orderableItemNotice: null,
					profileFilter: profileFilter,
					duplicateGearReq: null
				};
			},
			() => {
				// Always filter when the order type changes. We may be changing to/from Covid or to/from ship to office where Medicare filtering does not apply
				this.filterOrderableItems({
					preventDefault: () => {},
					target: {
						value: profileFilter
					}
				});
			}
		);
	}
	
	/**
	 * Switch to the Covid Registration order type and search and select the Covid product.
	 * If the Medicare Yes/No choice is displayed, we will delay the product search until after
	 * a Medicare choice is selected.
	 */
	startCovidRegistration = () => {
		const showMedicareChoice = !this.props.gearOrder.id && isMedicareSupported(this.props.gearOrder);
		const waitingOnMedicareChoice = showMedicareChoice && this.state.medicareOrder === null;

		this.setOrderType(this.orderTypes.COVIDREGISTRATION);

		if (!waitingOnMedicareChoice) {
			this.searchByOrderableItem(902);
		}
	}

	/**
	 * Sets the state's medicareOrder value.
	 * If medicareOrder is true and the current product has mixedComponentPricing, we also need to
	 * remove the panel selection and update the testSelectionForm.
	 * If the current order type is Covid Registration and the Covid OrderableItem has not been retrieved, 
	 * we need to fetch it and select the Covid product.
	 *
	 * @param medicareOrder the value to set
	 */
	setMedicareOrder = (medicareOrder) => {
		let testSelectionForm = Object.assign({}, this.state.testSelectionForm);
		if (medicareOrder && this.props.omsRequisition && this.props.omsRequisition.orderableItem.mixedComponentPricing) {
			testSelectionForm.panel.value = '';
		}

		this.setState({
			medicareOrder: medicareOrder,
			testSelectionForm: testSelectionForm,
			displayPricingType: null
		}, () => {
			if (this.state.orderType === this.orderTypes.COVIDREGISTRATION && !this.state.omsRequisition) {
				this.startCovidRegistration();
			}	
			if (!medicareOrder) {
				this.scrollToDetails();
			}
			this.filterOrderableItems({
				preventDefault: () => { },
				target: {
					value: this.state.profileFilter
				}
			});
		});
	}

	/**
	 * Returns whether the Medicare-filtered list of items should be displayed.
	 * The full list is displayed when the practitioner does not support Medicare, or when the order type is BULK (Ship to Office)
	 */
	isShowMedicareProducts = () => {
		return !!(isOrderRequisitionsMedicareEligible(this.props.gearOrder) && this.state.orderType !== this.orderTypes.BULK && (this.state.orderType || this.props.gearOrder.requisitions?.length));
	}

	/**
	 * Returns true when the Toxic Element Clearance profile is selected with Medicare billing and there is a possible Chelation provocation agent.
	 */
	isShowMedicareChelationWarning = () => {
		return (
			this.state.medicareOrder &&
			this.state.profileSpecificFields &&
			this.state.profileSpecificFields.compUrine &&
			(this.state.profileSpecificFields.compUrine.preProvocation?.value ||
				this.state.profileSpecificFields.compUrine.postProvocation?.value ||
				this.state.profileSpecificFields.compUrine.provocationAgent?.value)
		);
	}

	/**
	 * Build the initial map of edited CPT Code quantities from the Gear Requisition.
	 */
	buildCptQtyMap = (gearRequisition) => {
		// Include profileComponents and addOns in the map
		const options = [...gearRequisition?.componentOptions || [], ...gearRequisition?.addOnOptions || []];
		if (!options?.length) {
			return {};
		}

		return options.reduce((map, currentInputField) => {
			// Convert quantity to a String because that's what <CptQuantity> expects
			const quantity = currentInputField.cptQuantity ? `${currentInputField.cptQuantity}` : null;
			return {
				...map,
				...(quantity && { [currentInputField.omsInputFieldID]: quantity }), // Only include items with cptQuantity
			};
		}, {});
	};

	setEditingCptQuantity = () => {
		this.setState({ isEditingCptQuantity: true });
	}

	/**
	 * Returns true if the practitioner uses the international requisition layout
	 */
	isOfficeUsingInternationalRequisitionLayout = () => {
		return this.props.gearOrder?.practitioner?.office?.reqLayoutId === 3;
	}

	toggleResubmittalModal = () => {
		this.setState(prevState => {
			return {
				showResubmittalModal: !prevState.showResubmittalModal
			}
		});
	}

	/**
	 * SCR-7971
	 * Checks if the patient has completed the payment step and test selections have been changed in a way that might 
	 * increase the price.
	 */
	shouldWarnOfPriceIncrease = () => {

		/*
		 * Some workflows will not save paymentInfo (Medicare $0 or Client Bill). We still need to warn in this case
		 * if the patient has confirmed the order.
		 * If a payment was saved, make sure it came from the patient.
		 */
		const hasPayment = !!this.props.activeGearRequisition?.paymentInfo?.id;
		const patientPayment = this.props.activeGearRequisition?.paymentInfo?.paymentFrom === 'prc';

		return this.props.userInfo.type === 'myGDX' && // Only warn when a client changes test selections
			this.props.gearOrder?.workflow?.patientConfirmed && // Patient has completed the confirmation step
			(!hasPayment || patientPayment) && // If a payment was saved, it came from the patient
			!this.state.isConfirmedPriceIncrease &&
			!this.props.activeGearRequisition?.physicianBillingSelected && // Not Client Bill
			isPossiblePriceIncrease(this.props.activeGearRequisition, this.state.testSelectionForm, this.props.omsRequisition);
	}

	/**
	 * Handle the 'Yes' choice from the Price Increase Confirmation dialog.
	 * Flags that we have handled the price increase confirmation and attempts to submit the test selections.
	 */
	confirmPriceIncrease = () => {
		this.setState({
			showConfirmPriceIncrease: false,
			isConfirmedPriceIncrease: true
		}, () => {
			this.submitTestSelection();
		});
	}

	/**
	 * Handle the 'No' choice from the Price Increase Confirmation dialog.
	 * Close the confirmation modal and reset test selections to the original values.
	 */
	denyPriceIncrease = () => {
		this.setState({
			showConfirmPriceIncrease: false,
			isConfirmedPriceIncrease: false,
			submitDisabled: false,
			testSelectionForm: buildInitialTestSelectionForm(this.props.gearOrder, this.props.activeGearRequisition, this.props.omsRequisition, this.props.userInfo)
		});
	}

	closePriceIncreaseConfirmation = () => {
		this.setState({
			showConfirmPriceIncrease: false
		});
	}

	/**
	 * Store original requisition values and then load the new orderable item.
	 */
	repurposeCollectionPack = (omsOrderableItemId) => {
		/**
		 * repurposeDetails will be null the first time this method is called. In that situation, 
		 * the state.omsRequisition and state.activeGearRequisition will have the original values.
		 * Save those values for later because the state objects will be changed when we fetch the
		 * new orderable item.
		 */
		let repurposeDetails = this.state.repurposeDetails;
		if (!repurposeDetails) {
			repurposeDetails = {
				originalOrderableItemId: this.state.omsRequisition?.orderableItem?.id,
				activationCode: this.state.omsRequisition?.activationCode,
				reqNumber: this.state.omsRequisition?.id,
				gearReqId: this.state.activeGearRequisition?.id
			};

			this.setState({repurposeDetails: repurposeDetails});
		}

		if (repurposeDetails?.originalOrderableItemId === omsOrderableItemId && repurposeDetails.activationCode) {
			// We're switching back to the original item. Load the specific omsRequisition
			this.fetchOmsRequisitionForSearch(repurposeDetails.activationCode);
		} else {
			this.searchByOrderableItem(omsOrderableItemId);
		}
	}

	setComponentPriceMap = (componentPriceMap) => {
		this.setState({
			componentPriceMap: componentPriceMap
		});
	}

	setDisplayPricingType = (displayPricingType) => {
		this.setState({displayPricingType: displayPricingType})
	}

	render() {
		let patientRegistration = this.props.userInfo.type === 'prc';

		const internationalRequisitionLayout = this.isOfficeUsingInternationalRequisitionLayout();

		// Check if the practitioner supports Medicare
		const practitionerSupportsMedicare = isPractitionerSupportsMedicare(this.props.gearOrder?.practitioner);
		// Did a practitioner that does not support Medicare answer "Yes" to the Medicare question?
		const practitionerMedicareChoiceInvalid = this.state.medicareOrder && !practitionerSupportsMedicare;
		
		// Show Medicare Choice when practitioner supports Medicare and order type is not BULK and this is a new order
		const showMedicareChoice = !patientRegistration && this.isShowMedicareProducts() && !internationalRequisitionLayout && !this.props.inlineOrderType;
		// Delay showing product selection lists until the Medicare choice has been made
		// Also hide the list if the practitioner does not support Medicare but has selected "Yes" to the Medicare choice
		const waitingOnMedicareChoice = showMedicareChoice && (this.state.medicareOrder === null || practitionerMedicareChoiceInvalid );
		// Medicare is not valid if the requisition does not support Medicare
		const medicareChoiceIsValid = !(this.state.medicareOrder && this.props.userInfo.type === 'myGDX' && this.state.omsRequisition && this.state.omsRequisition.orderableItem && !isMedicareOrTricareOrderableItem(this.state.omsRequisition.orderableItem));
		// Use the full list or the Medicare filtered list depending on the medicareOrder setting
		const effectiveOrderableItemsLists = this.getEffectiveOrderableItemsLists();

		const recollectionOrder = !!this.props.gearOrder.recollectionFor;

		let showSearch = !this.props.inlineOrderType && (this.props.addNewReq || (!patientRegistration && (!this.props.activeGearRequisitionId || this.props.isShipToOfficeOrder( this.props.gearOrder ) || !medicareChoiceIsValid)));
		let showOrderableItemSearch = false;
		if( showSearch ) {
			showOrderableItemSearch = effectiveOrderableItemsLists !== null &&
				( ( effectiveOrderableItemsLists.otherOrderableItems || {} ).length > 0
					|| ( effectiveOrderableItemsLists.recentlyOrderedItems || {} ).length > 0 );
		}

		// Need to show the panel even when the Medicare choice is invalid when showSearch is false (Loading an existing order)
		// SCR-8162: Don't show panel selection when order type has not been selected and this isn't an existing order (prc users don't select order type)
		let showPanel = this.state.omsRequisition && (!waitingOnMedicareChoice) && (medicareChoiceIsValid || !showSearch) && (!!this.state.activeGearRequisition || !!this.state.orderType || this.props.userInfo.type === 'prc');
		let selectedPanel = (this.state.omsRequisition && this.state.omsRequisition.orderableItem) ? this.state.omsRequisition.orderableItem.name : null ;
		
		// Do not show ship to office if another type of order is being loaded (Can't mix and match ship-to-office with other registration types) or if this is a recollection order
		const displayShipToOfficeOption = (this.props.isShipToOfficeOrder( this.props.gearOrder ) || this.props.gearOrder?.requisitions?.length === 0) && !this.props.gearOrder.recollectionFor && !this.props.gearOrder.practitioner?.capAuthorized;

		// If a customization overlay should be displayed gather the required details
		let customizeOverlay = null;
		let customizedOrderableItemId = Number(this.state.customizationOverlayProperties.current);
		if( customizedOrderableItemId && ( this.state.customizationOverlayProperties[customizedOrderableItemId] || {} ).display ) {
			customizeOverlay = this.state.customizationOverlayProperties[customizedOrderableItemId];
			customizeOverlay.name = (customizeOverlay.omsRequisition && customizeOverlay.omsRequisition.orderableItem) 
				? customizeOverlay.omsRequisition.orderableItem.name
				: '';

			/*
			 * The customization modal expects a gearRequisition in order to show previous choices but for bulk orders
			 * we deal with the bulkOrderQuantityMap instead of actual gearRequisition objects.
			 * Map the relevant bulkOrder object into a gearRequisition object. The resulting req will not be a full
			 * gear requisition but it will have the data necessary for the customization overlay.
			 */
			let gearRequisition = this.state.bulkOrderQuantityMap.find( item => item.omsOrderableItemId === customizedOrderableItemId );
			if( gearRequisition ) {
				customizeOverlay.gearRequisition = gearRequisition;
				// Use the name from the gear req, when available, so we can display the name even before the oms req is loaded
				customizeOverlay.name = gearRequisition.omsOrderableItemName || '';
			}
		}

		const cptDisclaimer =
			this.state.medicareOrder &&
			this.state.omsRequisition &&
			this.state.omsRequisition.orderableItem &&
			this.state.omsRequisition.orderableItem.id === 260
				? '* CPT Code 86001 for IgG antibodies will only be billed once to Medicare for a total of 20 units. These units are not added together.'
				: null;

		// SCR-6843
		const nutritionalDiscardDisclaimer =
			this.state.omsRequisition?.orderableItem?.category?.toLowerCase() === 'nutritional' &&
			'Please note, specimens for patients less than 2 years of age will be discarded.';

		const showMedicareChelationWarning = this.isShowMedicareChelationWarning();

		const simplifiedClientBilling = this.state.simplifiedClientBilling;

		// The message to show if a client chooses "Yes" to Medicare but is not authorized to order Medicare
		const practitionerMedicareInvalidMessage = practitionerMedicareChoiceInvalid ? (
			<AlertMessage type="info">
				Medicare has defined healthcare provider degrees that are allowed to submit orders to be billed to Medicare. Your account does not meet the{' '}
				<a
					href="https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/Ordering-and-Certifying#eligible-specialty-types"
					target="_blank"
					rel="noopener noreferrer"
					style={{fontWeight: 'bold'}}
				>
					defined criteria.
				</a>{' '}
				In order to proceed with the patient's order, please update your selection to "No" and choose an appropriate non-insurance billing option.
				Should you believe that you have received this message erroneously or if you possess a qualified degree type that enables you to accept
				Medicare/Tricare beneficiaries, please do not hesitate to contact us at 800-522-4762. Our team will be glad to assist you in updating
				your account accordingly.
			</AlertMessage>
		) : null;

		const showBundles = !this.state.medicareOrder
			&& !this.state.profileFilter
			&& this.state.orderType === this.orderTypes.DROPSHIP
			&& !this.props.gearOrder.id
			&& !!effectiveOrderableItemsLists?.otherOrderableItems?.length;

		/**
		 * SCR-8383: Allow repurpose of a collection pack to a different test when:
		 * - Only for clients
		 * - Only for activations
		 * - Before the order is confirmed
		 * - Not a resubmittal or recollection
		 * - Original requisition is paperless and has an activation code
		 */
		const showRepurposeLink = !patientRegistration
			&& this.state.orderType === this.orderTypes.REGISTRATION
			&& !this.props.gearOrder.dataConfirmed
			&& !this.props.gearOrder.recollectionFor
			&& !this.props.activeGearRequisition?.xComId
			&& !this.state.xComId
			&& this.state.omsRequisition?.paperless
			&& this.state.omsRequisition?.activationCode;

		let pageTitle;
		if (!showSearch || recollectionOrder) {
			pageTitle = 'Panel Selection';
		} else if (patientRegistration) {
			pageTitle = 'Register another Requisition';
		} else {
			pageTitle = 'What would you like to do?';
		}

		return (
			<div>
				{/* Display the customization modal */}
				{ customizeOverlay && 
					<Modal
						id={customizeOverlay.orderableItemId + '-customization-modal'}
						title={'Customize ' + customizeOverlay.name}
						confirmAction={customizeOverlay.omsRequisition && 'Save'}
						cancelAction={'Cancel'}
						onSave={this.onCustomizationSave}
						modalHidden={() => this.toggleCustomizationOverlay(null)}
						doConfirm={this.state.customizedSelections && this.props.doConfirm}
					>
						{ (!customizeOverlay.omsRequisition && customizeOverlay.status === 'loading' ) && 
							<div className='text-center'><i className="fa fa-spinner fa-spin fa-fw" style={{"fontSize": "5em"}}></i></div>
						}
						{ (!customizeOverlay.omsRequisition && customizeOverlay.status === 'error' ) && 
							<div className='text-center'><span className='text-danger'>{customizeOverlay.errorMessage}</span></div>
						}
						{customizeOverlay.omsRequisition && 
							<PanelCustomization gearRequisition={customizeOverlay.gearRequisition} omsRequisition={customizeOverlay.omsRequisition}
								onSelection={this.onCustomizationSelection} displayPatientInstructions={false} shipToOffice={this.state.orderType === this.orderTypes.BULK} >
								<h4>Select a profile, components, or add-ons to pre-fill the test selection portion of the requisitions.</h4>
							</PanelCustomization>
						}
					</Modal>
				}
				{this.state.showReqImage &&
					<Lightbox
						mainSrc="/images/gear/Req-Activate-Numbers.jpg"
						imageTitle="Genova Diagnostics Requisition Form"
						onCloseRequest={() => this.setState({ showReqImage: false })}
						imageLoadErrorMessage="Requisition image failed to load"/>
				}
				<h2>{pageTitle}
					{(!showSearch && this.props.activeGearRequisition) && 
						<span>
							<br />
								<small>{this.props.getRequisitionTitle(this.state.omsRequisition)}</small>
						</span>}
				</h2>
				{(!showSearch && showMedicareChoice) && (
					<>
						<MedicareChoice gearOrder={this.props.gearOrder} userInfo={this.props.userInfo} disabled={this.state.disableInputs} onChange={this.setMedicareOrder} medicareOrder={this.state.medicareOrder} />
						{practitionerMedicareInvalidMessage}
					</>
				)}
				{/* Show search input, error messages, and order conflicts */}
				{showSearch &&
					<div>
						{/* reqistration type selection (req search, drop-ship, bulk-order) */}
						{(!patientRegistration && !recollectionOrder) && (
							<>
							{displayShipToOfficeOption && 
								<label className={"btn radius btn-primary " + (this.state.orderType === this.orderTypes.BULK ? "active": "")} style={{marginRight: '20px', 'boxShadow': '5px 5px 5px #6d95b7'}}>
						    		<input type="radio" className="hidden" name="reqType" defaultChecked={ this.state.orderType === this.orderTypes.BULK } onChange={ () => this.setOrderType(this.orderTypes.BULK) } /> 
						    		<span className="glyphicon glyphicon-shopping-cart"></span>  Ship to Office
						  		</label>
							}
							{this.state.displayDropShipOption &&
								<label className={"btn radius btn-dropship " + (this.state.orderType === this.orderTypes.DROPSHIP ? "active" : "")} style={{marginRight: '20px'}}>
								<input type="radio" className="hidden" name="reqType" defaultChecked={ this.state.orderType === this.orderTypes.DROPSHIP } onChange={ () => this.setOrderType(this.orderTypes.DROPSHIP) } /> 
								<span className="glyphicon glyphicon-shopping-cart"></span>  Ship to Patient
								</label>
							}
							{((this.state.displayDropShipOption && !this.props.gearOrder?.practitioner.capAuthorized) || internationalRequisitionLayout) && (
								<label className={"btn radius btn-register " + (this.state.orderType === this.orderTypes.REGISTRATION ? "active" : "")} style={{marginRight: '20px'}}>
						    		<input type="radio" className="hidden" name="reqType" checked={ this.state.orderType === this.orderTypes.REGISTRATION } onChange={ () => this.setOrderType(this.orderTypes.REGISTRATION) } />
						    		<span className="glyphicon glyphicon-edit"></span>  Activate Online
								</label>
							)}
							{this.props.gearOrder.practitioner.covidRegistrationAccount &&
								<label className={"btn radius btn-register-covid " + (this.state.orderType === this.orderTypes.COVIDREGISTRATION ? "active " : "")}>
						    	<input type="radio" className="hidden" name="reqType" defaultChecked={ this.state.orderType === this.orderTypes.COVIDREGISTRATION } onChange={ () => this.startCovidRegistration() } />
						    	<span className="glyphicon glyphicon-edit"></span>  Activate COVID Test
						  		</label>
							}
							</>
						)}
						{ (!this.state.orderType && ( this.state.gearOrder.id === 0 || this.state.gearOrder.requisitions.length === 0 ) ) && 
							<div style={{'height': '100px'}}></div>
						}
						{/* Current order details */}
						{(this.state.gearOrder && this.state.gearOrder.id !== 0 && this.state.orderType !== this.orderTypes.BULK && this.state.gearOrder.requisitions.length > 0 && !this.props.inlineOrderType) && 
							<div className='space-top'>
								<div className="panel panel-info">
									<div className="panel-heading">
										<h3 className="panel-title">Order #{this.state.gearOrder.id}</h3>
									</div>
									<div className="panel-body">
										<AlertMessage type={'warning'} display={this.state.gearOrder.containsShipToOfficeRequisition}>
											The previously selected products are not compatible with this type of order. 
											If you continue, the products selected to be shipped to your office will be
											canceled and removed from your order.
										</AlertMessage>
										<table className="table">
											<tbody>
												{this.state.gearOrder.requisitions.map( req => 
													<tr key={req.id}>
														<td>{req.reqNumber || req.quantity > 1 ? req.quantity + 'x' : ''}</td>
														<td>{req.omsOrderableItemName}</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						}
						{/* order conflicts */}
						{this.state.searchOrderConflict &&
							<div className="alert alert-warning">
								{patientRegistration && <div>
									<span>Requisition {this.state.searchOrderConflict.reqNumber} has previously been added to a different order. Please use the icons at the top of this page to continue your current order or <Link to='/'>click here to switch orders</Link>.</span>
								</div>}
								{!patientRegistration && <>
									<p className="space-bottom">Requisition {this.state.searchOrderConflict.reqNumber} has previously been added to order #{this.state.searchOrderConflict.order.id} and cannot be added to the current order.</p>
									<div className="col-md-4">
										<p>Please choose one of the following options:</p>
										<div className="radio">
											<label>
												<input type="radio" value="switch" name="orderConflict" id="conflict-switch" onClick={this.resolveOrderConflict} />
											Switch to order #{this.state.searchOrderConflict.order.id}
											</label>
										</div>
										<div className="radio">
											<label>
												<input type="radio" value="search" name="orderConflict" id="conflict-search" onClick={this.resolveOrderConflict} />
												Search for a different requistion
											</label>
										</div>
									</div>
									<div className="col-md-7">
										<div className="panel panel-info">
											<div className="panel-heading">
												<h3 className="panel-title">Order #{this.state.searchOrderConflict.order.id}</h3>
											</div>
											<div className="panel-body">
												<table className="table">
													<tbody>
														{this.state.searchOrderConflict.requisitions.map( req => 
															<tr key={req.reqNumber}>
																<td>{req.reqNumber}</td>
															<td>{req.reqName}</td>
															</tr>
														)}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								</>}
							</div>
						}
						{/* Search error messages */}
						<div className="alert alert-warning" hidden={!this.state.searchMessage}>
							{this.state.searchMessage}
						</div>
						{!medicareChoiceIsValid && 
							<div className="alert alert-warning">
								Medicare or Tricare billing is not available for the{' '}
								{(this.state.omsRequisition.orderableItem &&this.state.omsRequisition.orderableItem.name) ? <b>{this.state.omsRequisition.orderableItem.name}</b> : 'selected'}
								{' '}test. Please switch to Non-Medicare billing to order this test.
							</div>
						}
						{showMedicareChoice && (
							<>
								<MedicareChoice gearOrder={this.props.gearOrder} userInfo={this.props.userInfo} disabled={this.state.disableInputs} onChange={this.setMedicareOrder} medicareOrder={this.state.medicareOrder} />
								{practitionerMedicareInvalidMessage}
							</>
						)}
						{this.state.orderableItemNotice && (
							<div className="mt-20" dangerouslySetInnerHTML={{ __html: this.state.orderableItemNotice }} />
						)}

						{!waitingOnMedicareChoice && <div className="row">
							{/* Requisition number search */}
							{this.state.orderType === this.orderTypes.REGISTRATION && 
								<div className="col-sm-6">
									<form onSubmit={this.doReqSearch}>
										<div className={"form-group has-feedback " + (this.state.searchForm.requisitionNumber.valid === false ? "has-error" : "")}>
											<div className="row">
												<div className="col-sm-12">
													<label className="control-label" htmlFor='requisitionSearchBox'>Requisition or Activation Number</label>
													<input className="form-control" id='requisitionSearchBox' type='search'
														defaultValue={this.state.searchForm.requisitionNumber.value}
														onChange={this.onReqNumberChange} 
														autoComplete="off"
														ref={(input) => { this.searchInput = input }}/>
													{this.state.searchForm.requisitionNumber.valid === false &&
														<span className="validation-message text-danger">{this.state.searchForm.requisitionNumber.message}</span>
													}
												</div>
												<div className="col-xs-12">
													<a href="# " onClick={this.showReqImage}><small><em>Where do I find my number?</em></small></a>
													{this.props.userInfo.type === 'myGDX' && (
														<button
															className="btn btn-link btn-unstyled pull-right"
															type="button"
															style={{ minWidth: '50px', fontSize: 'inherit' }}
															onClick={(e) => {
																this.setOrderType(this.orderTypes.NO_CARD_ACTIVATION);
															}}
														>
															<small><em>I don't have a number</em></small>
														</button>
													)}
												</div>
												<div className="col-xs-12 space-top">
													<button className="btn btn-primary radius btn-sm margin-clear" >Begin<i className="pl-10 fa fa-arrow-circle-right"></i></button>
												</div>
											</div>
										</div>
									</form>
								</div>
							}
							{/* DropShip selection */}
							{(this.state.orderType === this.orderTypes.DROPSHIP || this.state.orderType === this.orderTypes.NO_CARD_ACTIVATION || this.state.orderType === this.orderTypes.UNKNOWN_ID_ACTIVATION) && (
								<div className="col-md-8" style={{marginTop: '20px'}}>
									{this.state.orderType === this.orderTypes.NO_CARD_ACTIVATION &&
										<AlertMessage type="warning" additionalCssClass="mt-0">
											<p>
												Select a product and we will generate a new Activation Code that you
												can use to activate your order.{' '}
												<strong>
													You will need to print the electronic requisition and include it
													when returning the collection pack
												</strong>
												.
											</p>
											<p>
												If your collection pack includes an Activation Card, please use the
												ID on that card. Utilizing this process while a card is also
												included in the collection pack will result in a STAR or your
												patient's specimens being discarded.
											</p>
										</AlertMessage>
									}
									{this.state.orderType === this.orderTypes.UNKNOWN_ID_ACTIVATION &&
										<AlertMessage type="warning" additionalCssClass="mt-0">
											<p>
												Please select the test you would like to activate.
											</p>
										</AlertMessage>
									}
									{(!patientRegistration && !this.state.medicareOrder && (this.state.orderType === this.orderTypes.DROPSHIP)) && (
										<ProductHighlights productHighlights={this.state.fullOrderableItemsLists?.productHighlights} />
									)}
									<div className="form-group">
										<label htmlFor='profileSearchBox' className={showOrderableItemSearch ? '' : 'text-muted'}>Select a product</label>
										<input className="form-control" id='profileSearchBox' type='search'
											value={this.state.profileFilter}
											onChange={this.filterOrderableItems} 
											autoComplete="off"
											placeholder="Search"
											disabled={!showOrderableItemSearch} />
									</div>
									<div style={{cursor: 'pointer'}}>
										<OrderableItemList 
											expandedOrderableItemList={this.state.expandedOrderableItemList} 
											orderableItems={effectiveOrderableItemsLists}
											filteredOrderableItemsList={this.state.filteredOrderableItemsList}
											profileFilter={this.state.profileFilter}
											quantityMap={this.state.bulkOrderQuantityMap}
											onAccordionToggle={this.onAccordionToggle}
											onQuantityUpdate={this.onQuantityUpdate}
											gsdlNo={this.props.gearOrder.practitioner.gsdlNo}
											displayQuantity={false}
											displayInfo={false}
											onPanelSelection={this.searchByOrderableItem}
											selectedProduct={selectedPanel} 
											dropShip={this.state.orderType === this.orderTypes.DROPSHIP}
											practShipToOfficePermitted={this.props.gearOrder.practitioner.shipToOfficePermitted}
											shipToOffice={this.state.orderType === this.orderTypes.BULK}
											bundleAccordion={!showBundles ? null : (
												<SavedBundlesAccordion
													savedBundles={this.state.fullOrderableItemsLists?.savedBundles}
													gearOrder={this.props.gearOrder}
													setGearOrder={this.props.setGearOrder} />
											)}
										/>
									</div>
								</div>
							)}
							{/* COVID selection */}
							{this.state.orderType === this.orderTypes.COVIDREGISTRATION && (
									<div className="col-md-8" style={{marginTop: '20px'}}>
										<div className="form-group">
											<label htmlFor='profileSearchBox' className={ showOrderableItemSearch ? '' : 'text-muted' }>Select a product</label>
											
											<input className="form-control" id='profileSearchBox' type='search'
												value={this.state.profileFilter}
												onChange={this.filterOrderableItems} 
												autoComplete="off"
												placeholder="Search"
												disabled={!showOrderableItemSearch} />
										</div>
										<div style={{cursor: 'pointer'}}>
											<OrderableItemList 
												expandedOrderableItemList={this.state.expandedOrderableItemList} 
												orderableItems={effectiveOrderableItemsLists}
												filteredOrderableItemsList={this.state.filteredOrderableItemsList}
												profileFilter={this.state.profileFilter}
												quantityMap={this.state.bulkOrderQuantityMap}
												onAccordionToggle={this.onAccordionToggle}
												onQuantityUpdate={this.onQuantityUpdate}
												gsdlNo={this.props.gearOrder.practitioner.gsdlNo}
												displayQuantity={false}
												displayInfo={false}
												onPanelSelection={this.searchByOrderableItem}
												selectedProduct={selectedPanel} 
												dropShip={this.state.orderType === this.orderTypes.DROPSHIP}
												practShipToOfficePermitted={this.props.gearOrder.practitioner.shipToOfficePermitted}
												shipToOffice={this.state.orderType === this.orderTypes.BULK}
												covidRegistration={this.state.orderType === this.orderTypes.COVIDREGISTRATION}
											/>
										</div>
									</div>
								)}
							{/* Ship to office selection */}
							{this.state.orderType === this.orderTypes.BULK && (
								<div id="bulk-order-wrapper">

									<div className="col-md-4 col-md-push-8">
										<OrderableItemSummary
											quantityMap={this.state.bulkOrderQuantityMap}
											onQuantityUpdate={this.onQuantityUpdate}
											onSubmit={this.submitBulkOrder}
											onCustomize={this.toggleCustomizationOverlay}
											overCanadaQtyLimit={this.state.overCanadaQtyLimit}
										/>
									</div>
									<div className="col-md-8 col-md-pull-4" style={{ marginTop: '20px' }}>
										{!patientRegistration && (
											<ProductHighlights productHighlights={this.state.fullOrderableItemsLists?.productHighlights} />
										)}
										<div className="form-group">
											<label htmlFor='profileSearchBox' className={ showOrderableItemSearch ? '' : 'text-muted' }>Select a product</label>
											<input className="form-control" id='profileSearchBox' type='search'
												value={this.state.profileFilter}
												onChange={this.filterOrderableItems} 
												autoComplete="off"
												placeholder="Search"
												disabled={!showOrderableItemSearch} />
										</div>
										
										<OrderableItemList 
											expandedOrderableItemList={this.state.expandedOrderableItemList} 
											orderableItems={effectiveOrderableItemsLists}
											filteredOrderableItemsList={this.state.filteredOrderableItemsList}
											profileFilter={this.state.profileFilter}
											quantityMap={this.state.bulkOrderQuantityMap}
											onAccordionToggle={this.onAccordionToggle}
											onQuantityUpdate={this.onQuantityUpdate}
											gsdlNo={this.props.gearOrder.practitioner.gsdlNo} 
											dropShip={this.state.orderType === this.orderTypes.DROPSHIP}
											practShipToOfficePermitted={this.props.gearOrder.practitioner.shipToOfficePermitted}
											shipToOffice={this.state.orderType === this.orderTypes.BULK}
										/>
									</div>
								</div>
							)}
						</div>}
					</div> 
				}
				{this.state.duplicateGearReq && (
					<div id="requisition-details">
						<h2><small>{this.state.duplicateGearReq?.omsOrderableItemName}</small></h2>
						<AlertMessage type='danger'>
							This product has already been added to the order. If you need to order the product multiple times, please do so on separate orders.
						</AlertMessage>
						<button type="submit" form="panels-form" className="btn btn-primary radius btn-sm margin-clear pull-right" onClick={()=>this.props.updateStep(this.props.gearOrder, 'panel', 'Next', this.props.activeGearRequisitionId)}>
							Next
							<i className="pl-10 fa fa-arrow-circle-right"></i>
						</button>
					</div>
				)}
				{showPanel &&
					<div id="requisition-details">
						{showSearch &&
								<>
									<h2><small>{this.props.getRequisitionTitle(this.state.omsRequisition)}</small></h2>
									{this.props.omsRequisition?.orderableItem?.specimenType &&
											<h3 style={{ margin: '0', marginTop: '-15px' }}><small>Sample Type: {this.props.omsRequisition.orderableItem.specimenType}</small></h3>
									}
								</>
						}
						{((this.props.userInfo.type === 'myGDX' && !this.props.inlineOrderType && !this.state.repurposeDetails)) &&
							<div className="mt-5">
								<ResubmittalLink resubmittalId={this.state.xComId} onClick={this.toggleResubmittalModal} disableEdit={this.state.disableInputs || recollectionOrder} />
							</div>
						}
						{this.state.disableInputs && (
							<AccessionedOrderAlert />
						)}
						{(this.state.medicareOrder && this.props.userInfo.type === 'myGDX') && 
							<MedicalNecessityDisclaimer>
								{' '}Therefore, when ordering tests for your Medicare patients, please check each test to be performed. If the full profile is necessary, please place a check next to each test.
								<MedicareObligationText />
								<div className={"text-right mt-5" + (this.state.medicalNecessityAcknowledged.valid === false ? " has-error" : "")}>
									<label className={"control-label mb-0"} style={{cursor: 'pointer'}} htmlFor="mnAcknowledgement">
										<input id="mnAcknowledgement" type="checkbox" value="true" name="mnAcknowledgement" 
											defaultChecked={!!this.state.medicalNecessityAcknowledged.value}
											required={true} onChange={this.setMedicalNecessityAcknowledged}
											aria-describedby='mnAcknowledgement-help' disabled={this.state.disableInputs} />
											{' '}I acknowledge I have read the Definition of Medical Necessity.<strong className="text-danger"> *</strong>
									</label>
									{this.state.medicalNecessityAcknowledged.valid === false && <div id="mnAcknowledgement-help" className="help-block mb-0 mt-0 validation-message">{this.state.medicalNecessityAcknowledged.message}</div>}
								</div>
							</MedicalNecessityDisclaimer>
						}
						{(patientRegistration && !simplifiedClientBilling && !this.state.prcPaperless) && (
							<>
								<AlertMessage type='info' display={patientRegistration}>
									<p>Cost estimates are only as accurate as the information you provide. 
										<br />
										We will only perform the test selections marked by your practitioner.
									</p>
								</AlertMessage>
								{this.state.omsRequisition?.activationCode ? (
									<div>
										If you have any questions about which test options you should select, please
										contact your physician or contact us at 800-522-4762.
									</div>
								) : (
									<div>
										Please check the test(s) and any add-ons your practitioner marked on your
										requisition form. If you have any questions about which test options you should
										select, please contact your physician or contact us at 800-522-4762.
									</div>
								)}
							</>
						)}
						{(this.state.prcPaperless && this.props.gearOrder?.patientRegistration) && (
							<AlertMessage type='info'>
								<p>Your healthcare provider has not submitted an order for this test. The base panel
									has been included by default. If this is not the test you were expecting, please
									reach out to your healthcare provider.</p>
							</AlertMessage>
						)}
						{(this.state.prcPaperless && !this.props.gearOrder?.patientRegistration) && (
							<AlertMessage type='info'>
								<p>Your healthcare provider has selected the tests to perform for this order. If this
									is not the test you were expecting, please reach out to your healthcare provider.</p>
							</AlertMessage>
						)}
						<div className="row">
							<div className="col-md-7">
								{!!this.state.omsRequisition?.orderableItem?.notice && (
									<div className="mt-20"
										dangerouslySetInnerHTML={{ __html: this.state.omsRequisition.orderableItem.notice }}
									></div>
								)}
								<form id="panels-form">
								{(!simplifiedClientBilling && !this.state.prcPaperless) && 
								<div className="clearfix">Select test and add-ons <strong className="text-danger"> *</strong>
									{(!patientRegistration && !this.state.medicareOrder && !internationalRequisitionLayout) && (
										<div className="pull-right">
											<DisplayPricingChoice
												displayPricingType={this.state.displayPricingType}
												setDisplayPricingType={this.setDisplayPricingType}
												componentPriceMap={this.state.componentPriceMap}
												setComponentPriceMap={this.setComponentPriceMap}
												orderableItem={this.state.omsRequisition?.orderableItem}
												practitioner={this.props.gearOrder?.practitioner} />
										</div>
									)}
									{patientRegistration &&
										<button type="button" className="btn btn-link btn-unstyled" onClick={this.showReqTestSelectionsImage}> <img src="/images/gear/QuestionMark_Small.jpg" alt="Question?" style={{'margin': 0, 'width': '15px', 'cursor': 'pointer', 'marginLeft': '5px'}} /></button>
									}
								</div>
								}
									<div className="well" >
										<div className="pull-right" style={{marginTop: '-15px', marginRight: '-10px'}}>
											<button type="button" className="btn-link btn-unstyled" title="Print Selections"
												onClick={this.handleDownloadPdf}><i className="fa fa-print padding-x-xs"></i></button>
										</div>
										{this.state.testSelectionForm.panel.valid === false &&
											<span className="validation-message text-danger">{this.state.testSelectionForm.panel.message}</span>
										}
										{cptDisclaimer && <div className="small mb-10">{cptDisclaimer}</div>}
										{nutritionalDiscardDisclaimer && <div className="small mb-10 text-warning"><strong>{nutritionalDiscardDisclaimer}</strong></div>}
										<div ref={this.printRef} className={"profile-list " + (this.state.testSelectionForm.panel.valid === false ? "has-error" : "") + (this.state.printTestSelections ? ' profile-list-print' : '')}>
											{this.state.omsRequisition.orderableItem.profileList.map(function(listValue, profileIndex){
												const allowPanelSelection = !(this.state.medicareOrder && this.state.omsRequisition.orderableItem.mixedComponentPricing);

												let profilePriceDisplay = getComponentPrice({
													priceType: this.state.displayPricingType,
													priceMap: this.state.componentPriceMap,
													calculateBasePrice: this.state.omsRequisition?.orderableItem?.mixedComponentPricing,
													inputFieldId: listValue.id
												});

												let rowClass = 'row checkbox';
												if (profilePriceDisplay) {
													rowClass += ' profile-price-row';
												}

												return (
													<div key={listValue.id}>
														{allowPanelSelection && (
															<div className={rowClass} style={{display: 'flex'}}>
																<div style={{flex: '1'}}>
																	<label>
																		{!listValue.displayOnly && (
																			<input type="checkbox" name="panelGroup" id={listValue.name} value={listValue.id} data-text={listValue.name}
																				checked={Number(listValue.id) === Number(this.state.testSelectionForm.panel.value)} onChange={this.selectPanel}
																				disabled={this.state.disableInputs || simplifiedClientBilling || this.state.prcPaperless} />
																		)}
																		<strong>{listValue.name} </strong>
																	</label>
																</div>
																{!!this.state.componentPriceMap && (
																	<div style={{whiteSpace: 'nowrap', marginRight: '4%'}}>
																		<ComponentPrice
																			inputField={listValue}
																			priceMap={this.state.componentPriceMap}
																			priceType={this.state.displayPricingType}
																			calculateBasePrice={this.state.omsRequisition?.orderableItem?.mixedComponentPricing}
																			showError={true} />
																	</div>
																)}
															</div>
														)}
														{(profileIndex === 0 && showRepurposeLink ) && (
															<RepurposeCollectionPack
																gbfSharedKitsMap={this.state.fullOrderableItemsLists?.gbfSharedKitsMap}
																orderableItem={this.state.omsRequisition?.orderableItem}
																handleRepurpose={this.repurposeCollectionPack}
																repurposeDetails={this.state.repurposeDetails ||
																{
																	originalOrderableItemId: this.state.omsRequisition?.orderableItem?.id,
																	activationCode: this.state.omsRequisition?.activationCode
																}} />
														)}
														<PanelComponents key={listValue.id}
															components={listValue.profileComponents}
															step={this.props.step}
															setDirty={this.props.setDirty}
															disabled={this.state.disableInputs || simplifiedClientBilling || this.state.prcPaperless}
															getProfileComponentIds={getProfileComponentIds}
															selectedComponents={this.state.testSelectionForm.components.value}
															setSelectedComponents={this.setSelectedComponents}
															selectedPanel={this.state.testSelectionForm.panel.value}
															showHeader={true}
															showCptQuantity={this.state.omsRequisition.orderableItem.containsCptCodeQuantity}
															splitCptQty={true}
															isMedicareOrder={this.state.medicareOrder}
															profile={listValue}
															cptQuantityMap={this.state.cptQuantityMap}
															updateCptQuantity={!patientRegistration && this.updateCptQuantity}
															isEditingCptQuantity={this.state.isEditingCptQuantity}
															setEditingCptQuantity={this.setEditingCptQuantity}
															priceMap={this.state.omsRequisition?.orderableItem?.mixedComponentPricing && this.state.componentPriceMap}
															priceDisplayType={this.state.displayPricingType}
														/>
														<PanelAddOns
															requisitionResponses={this.state.activeGearRequisition}
															addOns={listValue.addOnList}
															step={this.props.step}
															setDirty={this.props.setDirty}
															disabled={this.state.disableInputs || this.state.prcPaperless}
															selectedAddOns={this.state.testSelectionForm.addOns.value}
															setSelectedAddOns={this.setSelectedAddOns}
															showCptQuantity={this.state.omsRequisition.orderableItem.containsCptCodeQuantity}
															splitCptQty={true}
															shipToOffice={this.state.orderType === this.orderTypes.BULK}
															isMedicareOrder={this.state.medicareOrder}
															cptQuantityMap={this.state.cptQuantityMap}
															updateCptQuantity={!patientRegistration && this.updateCptQuantity}
															isEditingCptQuantity={this.state.isEditingCptQuantity}
															priceMap={this.state.componentPriceMap}
															priceDisplayType={this.state.displayPricingType}
														/>
														<ProfileDisclaimers omsInputField={listValue} />
												</div>)
											}, this)}
										</div>
									</div>
								</form>
								
							</div>
							<div className="space-top"> </div>	
							<div className="col-md-5">
								{ this.state.profileSpecificFields && <div className="well" id="profileSpecificFields">
									{ this.state.profileSpecificFields.collectionHormoneTherapy && <div id="collectionHormoneTherapy">
										Is the patient using any type of Hormone Replacement Therapy?<br/>
										<div className="radio-inline">
											<label className="radio">
												<input type="radio" name="hormoneTherapy" id="noHrt" 
													disabled={this.state.disableInputs} 
													value="false"
													data-fieldid={this.state.profileSpecificFields.collectionHormoneTherapy.noHrt.fieldId}
													defaultChecked={this.state.profileSpecificFields.collectionHormoneTherapy.noHrt.checked}/>No
											</label>
										</div>
										<div className="radio-inline">
											<label className="radio">
												<input type="radio" name="hormoneTherapy" id="hrt" 
													disabled={this.state.disableInputs} 
													value="true"
													data-fieldid={this.state.profileSpecificFields.collectionHormoneTherapy.hrt.fieldId}
													defaultChecked={this.state.profileSpecificFields.collectionHormoneTherapy.hrt.checked} />Yes
											</label>
										</div>
									</div>	}
									{ this.state.profileSpecificFields.cyclePhase && <div id="cyclePhase">
										<em>Physician: Check <strong>ONE</strong> description below.  Test cannnot be processed without this information.</em><br />
										{!!(this.state.profileSpecificFields.cyclePhase.premenopausal?.fieldId) && (
											<div className="radio">
												<label className="radio">
													<input type="radio" name="cyclePhaseOpt" id="premenopausal" 
														disabled={this.state.disableInputs} 
														data-fieldid={this.state.profileSpecificFields.cyclePhase.premenopausal.fieldId}
														defaultChecked={this.state.profileSpecificFields.cyclePhase.premenopausal.checked}/>Premenopausal woman- specimen collected during luteal phase (days 19-25)
												</label>
											</div>
										)}
										{!!(this.state.profileSpecificFields.cyclePhase.menopausalNoHRT?.fieldId) && (
											<div className="radio">
												<label className="radio">
													<input type="radio" name="cyclePhaseOpt" id="menopausalNoHRT" 
														disabled={this.state.disableInputs} 
														data-fieldid={this.state.profileSpecificFields.cyclePhase.menopausalNoHRT.fieldId}
														defaultChecked={this.state.profileSpecificFields.cyclePhase.menopausalNoHRT.checked}/>Menopausal woman- no hormone replacement therapy (HRT)
												</label>
											</div>
										)}
										{!!(this.state.profileSpecificFields.cyclePhase.menopausalHRT?.fieldId) && (
											<div className="radio">
												<label className="radio">
													<input type="radio" name="cyclePhaseOpt" id="menopausalHRT" 
														disabled={this.state.disableInputs} 
														data-fieldid={this.state.profileSpecificFields.cyclePhase.menopausalHRT.fieldId}
														defaultChecked={this.state.profileSpecificFields.cyclePhase.menopausalHRT.checked}/>Menopausal woman using hormone replacement therapy (HRT)
													</label>
											</div>	
										)}
										{!!(this.state.profileSpecificFields.cyclePhase.menopausalWoman?.fieldId) && (
											<div className="radio">
												<label className="radio">
													<input type="radio" name="cyclePhaseOpt" id="menopausalWoman" 
														disabled={this.state.disableInputs} 
														data-fieldid={this.state.profileSpecificFields.cyclePhase.menopausalWoman.fieldId}
														defaultChecked={this.state.profileSpecificFields.cyclePhase.menopausalWoman.checked}/>Menopausal woman
													</label>
											</div>	
										)}
									</div> }
									{ this.state.profileSpecificFields.compUrine && <div id="compUrine">
										<h4>Provocation:</h4>
										{ this.state.profileSpecificFields.compUrine.preProvocation && <div className="radio">
											<label>
												<input id="pre-provocation" name="provocation" type="radio" 
													disabled={this.state.disableInputs}
													data-fieldid={this.state.profileSpecificFields.compUrine.preProvocation.fieldId}
													data-fieldname='preProvocation'
													checked={this.state.profileSpecificFields.compUrine.preProvocation.value}
													value="@PRE"
													onChange={this.updateCompUrineProvocation} 
													onClick={this.updateCompUrineProvocation}/>
													Pre-Provocation
											</label>
										</div> }
										{ this.state.profileSpecificFields.compUrine.postProvocation && <div className="radio">
											<label>
												<input id="post-provocation" name="provocation" type="radio" 
													disabled={this.state.disableInputs} 
													data-fieldid={this.state.profileSpecificFields.compUrine.postProvocation.fieldId}
													data-fieldname='postProvocation'
													checked={this.state.profileSpecificFields.compUrine.postProvocation.value}
													value="@POST"
													onChange={this.updateCompUrineProvocation}
													onClick={this.updateCompUrineProvocation}
													 />
													Post-Provocation
											</label>	
										</div> }
										<small><i>All results will be reported in mcg/g creatinine</i></small>
										{showMedicareChelationWarning && 
											<div className="row mt-5">
												<div className="col-xs-12 alert alert-warning bold">
													This laboratory service is statutorily non-covered by Medicare when the patient is on chelation therapy.  Please select the no insurance or practitioner billing option on the billing options step if you have a Medicare patient on chelation therapy.
												</div>
											</div>
										}
									</div> }
									{ this.state.profileSpecificFields.covid19 && <div id="covid19">
										{/* Specimen Collection Type */}
										{this.state.profileSpecificFields.covid19['Collection Type-Other'] && (
											<div className="form-group">
												<h4>Specimen Collection Type</h4>
												<div className="row">
													<div className="col-xs-6">
														<label className="radio-inline">
															<input
																type="radio"
																name="CovidSampleCollectionType"
																id="SampleCollectionType-NP"
																value="@NP"
																data-fieldid={this.state.profileSpecificFields.covid19['Collection Type-Nasopharyngeal (NP)'].fieldId}
																data-fieldname='SampleCollectionType-NP'
																disabled={this.state.disableInputs}
																checked={this.state.profileSpecificFields.covid19['Collection Type-Nasopharyngeal (NP)'].value === 'true'}
																onChange={this.updateCovidCollectionType} />
															Nasopharyngeal (NP)
														</label>
													</div>
													<div className="col-xs-6">
														<label className="radio-inline">
															<input
																type="radio"
																name="CovidSampleCollectionType"
																id="SampleCollectionType-OP"
																value="@OP"
																data-fieldid={this.state.profileSpecificFields.covid19['Collection Type-Oropharyngeal (OP)'].fieldId}
																data-fieldname='SampleCollectionType-OP'
																disabled={this.state.disableInputs}
																checked={this.state.profileSpecificFields.covid19['Collection Type-Oropharyngeal (OP)'].value === 'true'}
																onChange={this.updateCovidCollectionType} />
															Oropharyngeal (OP)
														</label>
													</div>
													<div className="col-xs-6">
														<label className="radio-inline">
															<input
																type="radio"
																name="CovidSampleCollectionType"
																id="SampleCollectionType-NA"
																value="@NA"
																data-fieldid={this.state.profileSpecificFields.covid19['Collection Type-Nasal Anterior'].fieldId}
																data-fieldname='SampleCollectionType-NA'
																disabled={this.state.disableInputs}
																checked={this.state.profileSpecificFields.covid19['Collection Type-Nasal Anterior'].value === 'true'}
																onChange={this.updateCovidCollectionType} />
															Nasal Anterior
														</label>
													</div>
													<div className="col-xs-6">
														<label className="radio-inline">
															<input
																type="radio"
																name="CovidSampleCollectionType"
																id="SampleCollectionType-NM"
																value="@NM"
																data-fieldid={this.state.profileSpecificFields.covid19['Collection Type-Nasal Midtubinar'].fieldId}
																data-fieldname='SampleCollectionType-NM'
																disabled={this.state.disableInputs}
																checked={this.state.profileSpecificFields.covid19['Collection Type-Nasal Midtubinar'].value === 'true'}
																onChange={this.updateCovidCollectionType} />
															Nasal Midtubinar
														</label>
													</div>
													<div className="col-xs-12">
														<label className="radio-inline">
															<input
																type="radio"
																name="CovidSampleCollectionType"
																id="SampleCollectionType-OTHR"
																value="@OTHR"
																data-fieldid={this.state.profileSpecificFields.covid19['Collection Type-Other'].fieldId}
																data-fieldname='SampleCollectionType-OTHR'
																disabled={this.state.disableInputs}
																checked={this.state.profileSpecificFields.covid19['Collection Type-Other'].value === 'true'}
																onChange={this.updateCovidCollectionType} />
															Other
															{(this.state.profileSpecificFields.covid19['Collection Type-Other'].value === 'true') && (
																<input 
																	type="text"
																	style={{ marginLeft: '10px' }}
																	maxLength="50"
																	size="30"
																	placeholder="Please Specify"
																	disabled={this.state.disableInputs}
																	value={this.state.profileSpecificFields.covid19['Collection Type-Other_Specific'] ?? ''}
																	onChange={this.handleCovidCollectionTypeOther}
																/>
															)}
														</label>
													</div>
												</div>
											</div>
										)}
										<h4><p>Federally Required Questions</p></h4>
										<ol className='pl-20'>
											{/* Diagnosed yes/no does not appear on all Covid reqs */}
											{this.state.profileSpecificFields.covid19['Diagnosed-yes'] &&
												<li>
													Has patient been diagnosed with a known current or known prior COVID-19 infection or suspected current or suspected past COVID-19 infection?
													<div className="radio">
														<label>
															<input type="radio" name="diagnosed" id="diagnosed-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Diagnosed-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Diagnosed-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio">
														<label>
															<input type="radio" name="diagnosed" id="diagnosed-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Diagnosed-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Diagnosed-no'].fieldId} />
															No {this.state.medicareOrder && <span> - Completed ABN form is required for Medicare</span>}
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['First Test-yes'] &&
												<li className="mb-10">
													Is this the patient's first COVID-19 (SARS-CoV-2) test?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="first-test" id="first-test-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['First Test-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['First Test-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="first-test" id="first-test-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['First Test-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['First Test-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="first-test" id="first-test-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['First Test-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['First Test-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Healthcare-yes'] &&
												<li className="mb-10">
													Is the patient currently employed and working in healthcare?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="healthcare" id="healthcare-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Healthcare-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Healthcare-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="healthcare" id="healthcare-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Healthcare-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Healthcare-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="healthcare" id="healthcare-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Healthcare-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Healthcare-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Symptoms-yes'] &&
												<li className="mb-10">
													Is the patient experiencing symptoms as defined by the CDC?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="symptoms" id="symptoms-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Symptoms-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Symptoms-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="symptoms" id="symptoms-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Symptoms-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Symptoms-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="symptoms" id="symptoms-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Symptoms-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Symptoms-unknown'].fieldId} />
															Unknown
														</label>
													</div>
													<div>
														If Yes, please provide the date of symptom onset
														<br />
														<input type="text" name="symptoms-date" id="symptoms-date" 
															maxLength={10} placeholder="mm/dd/yyyy"
															disabled={this.state.disableInputs}
															defaultValue={this.state.profileSpecificFields.covid19['Symptoms Date'].value} 
															data-fieldid={this.state.profileSpecificFields.covid19['Symptoms Date'].fieldId} />
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Hospitalized-yes'] &&
												<li className="mb-10">
													Is the patient currently hospitalized due to COVID-19 (SARS-CoV-2)?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="hospitalized" id="hospitalized-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Hospitalized-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Hospitalized-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="hospitalized" id="hospitalized-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Hospitalized-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Hospitalized-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="hospitalized" id="hospitalized-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Hospitalized-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Hospitalized-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['ICU-yes'] &&
												<li className="mb-10">
													Is the patient currently in the ICU due to COVID-19 (SARS-CoV-2)?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="icu" id="icu-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['ICU-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['ICU-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="icu" id="icu-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['ICU-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['ICU-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="icu" id="icu-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['ICU-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['ICU-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Resident-yes'] &&
												<li className="mb-10">
													Is the patient currently a resident in a congregate care setting (including nursing homes, residential care for people with intellectual and developmental disabilities, psychiatric treatment facilities, group homes, board and care homes, homeless shelter, foster care or other setting)?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="resident" id="resident-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Resident-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Resident-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="resident" id="resident-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Resident-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Resident-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="resident" id="resident-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Resident-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Resident-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Pregnant-yes'] &&
												<li className="mb-10">
													Is the patient currently pregnant?
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="pregnant" id="pregnant-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Pregnant-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Pregnant-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="pregnant" id="pregnant-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Pregnant-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Pregnant-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="pregnant" id="pregnant-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Pregnant-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Pregnant-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Onset of Illness-yes'] &&
												<li className="mb-10">
													Is this to support diagnosis of COVID-19 9-14 days after onset of illness?  
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="illness" id="illness-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Onset of Illness-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Onset of Illness-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="illness" id="illness-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Onset of Illness-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Onset of Illness-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="illness" id="illness-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Onset of Illness-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Onset of Illness-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
											{this.state.profileSpecificFields.covid19['Inflammatory in Child-yes'] &&
												<li className="mb-10">
													Is this to support diagnosis of multisystem inflammatory syndrome in a child?  
													<br />
													<div className="radio-inline pl-15">
														<label className="radio-inline pl-5">
															<input type="radio" name="child" id="child-yes" 
																value="true" 
																disabled={this.state.disableInputs}
																defaultChecked={this.state.profileSpecificFields.covid19['Inflammatory in Child-yes'].value === 'true'}
																data-fieldid={this.state.profileSpecificFields.covid19['Inflammatory in Child-yes'].fieldId} />
															Yes
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="child" id="child-no" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Inflammatory in Child-no'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Inflammatory in Child-no'].fieldId} />
															No
														</label>
													</div>
													<div className="radio-inline">
														<label className="radio-inline">
															<input type="radio" name="child" id="child-unknown" 
																	value="true" 
																	disabled={this.state.disableInputs}
																	defaultChecked={this.state.profileSpecificFields.covid19['Inflammatory in Child-unknown'].value === 'true'}
																	data-fieldid={this.state.profileSpecificFields.covid19['Inflammatory in Child-unknown'].fieldId} />
															Unknown
														</label>
													</div>
												</li>
											}
										</ol>
									</div>	}
								</div> }
							</div>
						</div>
						<RequiredFieldDisclaimer />
						<div className="col-md-12 sticky-bottom">
							{(!this.props.inlineOrderType) && (
								<>
									<button type="button" className="btn btn-white radius btn-sm margin-clear" onClick={this.goBack}>
										<i className="pl-10 fa fa-arrow-circle-left"></i> &nbsp;
										Back
									</button>
									&nbsp;
								</>
							)}
							{/* Don't display the Cancel button if it's an inlineOrder and we have an activate reqId */}
							{(this.props.inlineOrderType && !this.props.activeGearRequisitionId) && (
								<>
									<button type="button" className="btn btn-white radius btn-sm margin-clear" onClick={this.goBack}>
										Cancel
									</button>
								</>
							)}
					   		<button type="submit" form="panels-form" className="btn btn-primary radius btn-sm margin-clear pull-right" onClick={this.submitTestSelection} disabled={this.state.submitDisabled}>
							   {!!this.props.inlineOrderType ? 'Save' : 'Next'}
								<i className="pl-10 fa fa-arrow-circle-right"></i>
							</button>
						</div> 
					</div>
				}
				{this.state.displayLoadOrder && <LoadOrderFromSearch orderToLoad={this.state.orderToLoad} reqToLoad={this.state.reqToLoad} loadOrder={this.loadOrder} displayLoadOrderMedicareDetails={this.state.displayLoadOrderMedicareDetails} modalHidden={() => this.setState({ displayLoadOrder: false })} />}
				{this.state.showReqTestSelectionsImage && <Lightbox mainSrc="/images/gear/req-test-selections.png" imageTitle="Genova Diagnostics Requisition Form" onCloseRequest={() => this.setState({ showReqTestSelectionsImage: false })} imageLoadErrorMessage="Requisition image failed to load" />}

				{/* Display the resubmittal modal */}
				{this.state.showResubmittalModal && 
					<ResubmittalPrompt resubmittalId={this.state.xComId} disableInputs={this.state.disableInputs} onChange={this.onXComIdChange} onClose={this.toggleResubmittalModal} />
				}

				{/* Display the price increase confirmation modal */}
				{this.state.showConfirmPriceIncrease && (
					<Modal
						title="Please Confirm Possible Price Increase"
						confirmAction="Yes"
						cancelAction="No"
						onSave={this.confirmPriceIncrease}
						onCancel={this.denyPriceIncrease}
						modalHidden={this.closePriceIncreaseConfirmation}
					>
						<div>
							You are about to make changes to an online order which has already been confirmed by your
							patient. Depending on the billing option used, these changes may result in a higher cost of
							services.
							<br />
							<br />
							<div className="text-right">Continue with these changes?</div>
						</div>
					</Modal>
				)}
			</div> );
	}
}

// wrap the component in the workflow and requisition step HOCs to get access to common workflow/req logic
export default withWorkflow(withRequisitionStep(Panels));
